import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  ActivityActorSchema,
  ActivityFieldDiffSchema,
  activityPageInputShape,
  activityPageResponseSchema,
} from "./activityTimeline.contract";
import {
  StepDefinitionSchema,
  EdgeDefinitionSchema,
  StepLayoutGroupSchema,
  ZipTieSchema,
  WorkflowRunStatusSchema,
  StepRunStatusSchema,
  StepCategorySchema,
  StepLibraryModuleSchema,
  SocketDefinitionSchema,
  FORM_FIELD_TYPES,
  collectResultsOutputSchema,
  MAX_CHANGELOG_CHARS,
  normalizeWorkflowEditTrail,
} from "@erp/workflows";
import { JsonWireValueSchema, SystemEntity } from "@erp/shared-types";
import {
  sandboxInstanceSchema,
  sandboxSpanSchema,
  sandboxLogLineSchema,
} from "./sandboxes.contract";
import { ToolCallRunResponseSchema } from "./agent-definitions.contract";

/**
 * Orchestrator type for workflow execution
 */
export const OrchestratorTypeSchema = z.literal("temporal");

/**
 * @deprecated Workflow tags were removed. Kept on responses so existing API
 * consumers still validate; always an empty array. Drop together with the
 * `wfv2_workflows.tags` column.
 */
const DeprecatedWorkflowTagsSchema = z.array(z.string()).meta({
  deprecated: true,
  description:
    "Deprecated: workflow tags were removed. Always an empty array; will be dropped in a future version.",
});

/**
 * Workflow definition creation request
 */
export const CreateWorkflowDefinitionRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  steps: z.array(StepDefinitionSchema),
  edges: z.array(EdgeDefinitionSchema),
  /** Purely visual step layout groups. Never validated or executed. */
  stepLayoutGroups: z.array(StepLayoutGroupSchema).optional(),
  /** Visual straps bundling edges into ribbons. Never validated or executed. */
  zipTies: z.array(ZipTieSchema).optional(),
  orchestratorType: OrchestratorTypeSchema.optional(),
  /** Org namespace (`namespaces.*`) to file the new workflow under. */
  namespaceId: z.uuid().nullable().optional(),
  /** When true (default), validates the graph before persisting. Draft saves skip validation. */
  validateGraph: z.boolean().optional(),
});

/**
 * Saved test case for manual workflow testing
 */
export const WorkflowTestCaseSchema = z.object({
  id: z.uuid(),
  workflowDefinitionId: z.uuid(),
  createdBy: z.uuid(),
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  eventData: z.record(z.string(), z.unknown()),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type WorkflowTestCase = z.infer<typeof WorkflowTestCaseSchema>;

/**
 * Test case CRUD schemas
 */
export const CreateTestCaseRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  eventData: z.record(z.string(), z.unknown()),
});

export const UpdateTestCaseRequestSchema = z.object({
  testCaseId: z.uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  eventData: z.record(z.string(), z.unknown()).optional(),
});

export const DeleteTestCaseRequestSchema = z.object({
  testCaseId: z.uuid(),
});

export const ListTestCasesRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
});

export const RunTestCaseRequestSchema = z.object({
  testCaseId: z.uuid(),
  /** Trigger step to start from; required when the run version has several. */
  triggerStepId: z.uuid().optional(),
});

export const CheckTestCaseCompatibilityRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  steps: z.array(StepDefinitionSchema),
});

export const CheckTestCaseCompatibilityResponseSchema = z.object({
  compatible: z.array(WorkflowTestCaseSchema),
  incompatible: z.array(WorkflowTestCaseSchema),
});

/**
 * Workflow definition update request — version-content only.
 *
 * Lineage-level fields (name, description, isActive, orchestratorType)
 * moved to updateWorkflow. This endpoint now only accepts draft-editable
 * content: steps and edges. Targets a specific WorkflowDefinition row.
 */
export const UpdateWorkflowDefinitionRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  steps: z.array(StepDefinitionSchema).optional(),
  edges: z.array(EdgeDefinitionSchema).optional(),
  /**
   * Purely visual step layout groups. Independent of `steps`/`edges` — a save may
   * carry groups only (rename/recolour/move a group box). Omitting the key
   * leaves the stored groups untouched.
   */
  stepLayoutGroups: z.array(StepLayoutGroupSchema).optional(),
  /**
   * Visual straps bundling edges into ribbons. Omitting the key on an edge
   * rewrite prunes membership against the new edges; sending an array replaces
   * the stored set.
   */
  zipTies: z.array(ZipTieSchema).optional(),
  /** When true, validates the graph before persisting (e.g. publish). Draft saves skip validation. */
  validateGraph: z.boolean().optional(),
});

/**
 * Validate a draft's graph WITHOUT persisting or publishing. Runs the same
 * graph validation the publish path (`updateDefinition` with `validateGraph:
 * true`) runs, but returns every error instead of throwing on the first — so
 * the editor can highlight all problems at once.
 */
export const ValidateWorkflowDefinitionRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  steps: z.array(StepDefinitionSchema),
  edges: z.array(EdgeDefinitionSchema),
});

export const WorkflowValidationErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  /** Node ids to highlight for this error. */
  nodeIds: z.array(z.string()).optional(),
  /** Sockets involved, so the editor can point at specific inputs. */
  sockets: z
    .array(z.object({ stepId: z.string(), socketId: z.string() }))
    .optional(),
});

export const ValidateWorkflowDefinitionResponseSchema = z.object({
  valid: z.boolean(),
  errors: z.array(WorkflowValidationErrorSchema),
});

/**
 * Non-blocking validation findings returned by save / publish / finalize while
 * the server's validation-authority checks are in "warn" enforcement (see
 * backend `VALIDATION_AUTHORITY_ENFORCEMENT`). Same shape as a validation
 * error; the validate endpoints report the same findings as errors. Omitted
 * when there are none.
 */
export const WorkflowValidationWarningsSchema = z
  .array(WorkflowValidationErrorSchema)
  .optional();

/**
 * Workflow update request — workflow-level (lineage-level) fields.
 *
 * Applied immediately to the Workflow row, affecting every version's
 * display + trigger behavior. Independent of which version is viewed.
 */
export const UpdateWorkflowRequestSchema = z.object({
  workflowId: z.uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  isComponent: z.boolean().optional(),
  exposedInputs: z.array(z.string()).nullable().optional(),
  exposedOutputs: z.array(z.string()).nullable().optional(),
  orchestratorType: OrchestratorTypeSchema.optional(),
  fanoutStrategy: z.enum(["parallel", "sequential"]).optional(),
  retryConfig: z.record(z.string(), z.unknown()).nullable().optional(),
  workflowManagerAgentDefinitionId: z.uuid().nullable().optional(),
  /** Org namespace grouping (`namespaces.*`); null clears it. */
  namespaceId: z.uuid().nullable().optional(),
});

/**
 * Workflow definition duplicate request
 */
export const DuplicateWorkflowDefinitionRequestSchema = z.object({
  workflowDefinitionId: z.string().uuid(),
  name: z.string().min(1).max(255),
});

/**
 * Versioning lifecycle status. See docs/projectplans/workflow-versioning.md.
 */
export const WorkflowDefinitionStatusSchema = z.enum(["draft", "finalized"]);

/**
 * How a published agent uses one workflow.
 *
 * `setup` / `teardown` are task-loop phases. `action` is a workflow bound on
 * the agent as a tool (`config.workflowActions`).
 */
export const AgentWorkflowRoleSchema = z.enum(["setup", "teardown", "action"]);

/**
 * One published agent that depends on a workflow. Absent roles are not listed.
 * A paused agent is included: pausing it does not drop the dependency.
 */
export const AgentWorkflowUsageSchema = z.object({
  agentId: z.uuid(),
  agentName: z.string(),
  isActive: z.boolean(),
  roles: z.array(AgentWorkflowRoleSchema).min(1),
});

export type AgentWorkflowRole = z.infer<typeof AgentWorkflowRoleSchema>;
export type AgentWorkflowUsage = z.infer<typeof AgentWorkflowUsageSchema>;

/**
 * Workflow (lineage-level entity) response.
 *
 * Workflow-level fields — name, description, isActive, orchestratorType, etc.
 * — live here. One Workflow owns many WorkflowDefinition rows (versions).
 * `pinnedVersionId` identifies the canonical version used by triggers.
 */
export const WorkflowResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  /** @deprecated Always `[]`. */
  tags: DeprecatedWorkflowTagsSchema,
  isActive: z.boolean(),
  isComponent: z.boolean(),
  exposedInputs: z.array(z.string()).nullable().optional(),
  exposedOutputs: z.array(z.string()).nullable().optional(),
  orchestratorType: OrchestratorTypeSchema,
  fanoutStrategy: z.enum(["parallel", "sequential"]),
  retryConfig: z.record(z.string(), z.unknown()).nullable().optional(),
  workflowManagerAgentDefinitionId: z.uuid().nullable().optional(),
  /** Org namespace this workflow is grouped under (`namespaces.*`), if any. */
  namespaceId: z.uuid().nullable().optional(),
  pinnedVersionId: z.uuid().nullable().optional(),
  // Derived from run history (highest finalized version with a successful
  // root run) — computed by getWorkflow, not stored on the row.
  lastKnownGoodVersionId: z.uuid().nullable().optional(),
  /**
   * Published agents that run this workflow as a setup phase, a teardown
   * phase, or an action tool. Omitted when none do. Draft agent versions are
   * not included — only the published configuration runs.
   */
  agentUsage: z.array(AgentWorkflowUsageSchema).optional(),
  createdBy: z.uuid(),
  createdByName: z.string().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/**
 * Workflow definition response (a single immutable-once-finalized version).
 *
 * Lineage-level fields (name/description/isActive/orchestratorType) live
 * on the Workflow, not here — fetch them via `getWorkflow(workflowId)` when
 * you need them. `steps` and `edges` are the graph snapshot this version
 * executes.
 */
export const WorkflowDefinitionResponseSchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
  parentVersionId: z.uuid().nullable().optional(),
  version: z.number(),
  status: WorkflowDefinitionStatusSchema,
  // Derived at mapping time: `true` when this version's id matches the
  // owning Workflow's pinnedVersionId.
  isPinned: z.boolean(),
  changelog: z.string().nullable().optional(),
  createdBy: z.uuid(),
  steps: z.array(StepDefinitionSchema),
  edges: z.array(EdgeDefinitionSchema),
  /** Purely visual step layout groups drawn behind the nodes. Never executed. */
  stepLayoutGroups: z.array(StepLayoutGroupSchema),
  /** Visual straps bundling edges into ribbons. Never executed. */
  zipTies: z.array(ZipTieSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/** What create / update return: the saved version plus any non-blocking findings. */
export const SaveWorkflowDefinitionResponseSchema =
  WorkflowDefinitionResponseSchema.extend({
    warnings: WorkflowValidationWarningsSchema,
  });

/**
 * Trigger workflow request
 */
export const TriggerWorkflowRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  initialInputs: z.record(z.string(), z.unknown()).optional(),
  rerunOfWorkflowRunId: z.uuid().optional(),
  /**
   * Which trigger step starts the run. Optional when the version has at most
   * one trigger; required when it has several (only the chosen trigger's
   * branch runs — the others are pruned for this run).
   */
  triggerStepId: z.uuid().optional(),
});

/**
 * Workflow run response
 */
/** What a run's Collect Results step recorded (see collect-results.step.ts). */
export const CollectedResultsSchema = z.object({
  collectStepId: z.uuid(),
  summary: collectResultsOutputSchema.shape.summary,
  failed: collectResultsOutputSchema.shape.failed,
});
export type CollectedResults = z.infer<typeof CollectedResultsSchema>;

export const WorkflowRunResponseSchema = z.object({
  id: z.uuid(),
  workflowDefinitionId: z.uuid(),
  // Workflow (lineage) id — denormalized on the run row for fast grouping.
  workflowId: z.uuid(),
  /**
   * Name of the owning workflow. Present only when the caller joined the
   * workflow through the definition (the cross-workflow runs list needs it;
   * the per-workflow tab already knows the name).
   */
  workflowName: z.string().optional(),
  // Snapshot of the version this run executed against (joined at read time).
  // Version number is stable for any given run since definitions are immutable.
  workflowDefinitionVersion: z.number().optional(),
  workflowDefinitionIsPinned: z.boolean().optional(),
  workflowDefinitionChangelog: z.string().nullable().optional(),
  status: WorkflowRunStatusSchema,
  /**
   * Descendant runs (fanout children, recursively) that are still open.
   *
   * A detached fanout closes its parent as soon as the children have started,
   * so a parent row can read "completed" with work still in flight. Read
   * surfaces that compute this report `status: "running"` instead; the count
   * is exposed so the UI can say how many children are still going. Absent
   * when the caller didn't compute it.
   */
  openDescendantRunCount: z.number().int().optional(),
  triggeredBy: z.uuid().nullable(), // null for system-triggered workflows
  triggeredByName: z.string().optional(), // User's display name if triggered by a user
  triggeredAt: z.iso.datetime(),
  startedAt: z.iso.datetime().optional(),
  completedAt: z.iso.datetime().optional(),
  stopReason: z.string().optional(),
  errorStepId: z.uuid().optional(),
  totalSteps: z.number(),
  completedSteps: z.number(),
  wallTimeMs: z.number().optional(),
  userTimeMs: z.number().optional(),
  systemTimeMs: z.number().optional(),
  sentryTraceId: z.string().optional(),
  // Parent-child workflow support
  parentWorkflowRunId: z.uuid().optional(),
  parentContext: z.record(z.string(), z.unknown()).optional(),
  rerunOfWorkflowRunId: z.uuid().optional(),
  hasBeenRerun: z.boolean().optional(),
  // When status is "scheduled", the future time this rerun is due to fire.
  scheduledFor: z.iso.datetime().optional(),
  // True when this run has a future scheduled rerun queued against it.
  hasUpcomingScheduledRerun: z.boolean().optional(),
  testCaseId: z.uuid().optional(),
});

/**
 * Get workflow run request
 */
export const GetWorkflowRunRequestSchema = z.object({
  workflowRunId: z.uuid(),
});

/**
 * Step run response
 */
export const StepRunResponseSchema = z.object({
  id: z.uuid(),
  workflowRunId: z.uuid(),
  stepDefinitionId: z.uuid(),
  attemptNumber: z.number(),
  status: StepRunStatusSchema,
  inputData: z.record(z.string(), z.unknown()),
  outputData: z.record(z.string(), z.unknown()).optional(),
  queuedAt: z.iso.datetime().optional(),
  startedAt: z.iso.datetime().optional(),
  completedAt: z.iso.datetime().optional(),
  duration: z.number().optional(),
  stopReason: z.string().optional(),
  sentrySpanId: z.string().optional(),
  /**
   * Set when this step was NOT executed — its result was copied from a prior
   * run by a replayed rerun. Points at the exact source StepRun (a (run, step)
   * pair can hold one row per retry attempt).
   */
  replaySourceStepRunId: z.uuid().optional(),
  /**
   * The run the replay source belongs to, resolved server-side so the trace can
   * link straight to it. Only populated by the trace endpoints.
   */
  replaySourceWorkflowRunId: z.uuid().optional(),
  /** Linked agent run ID for background_agent steps */
  agentRunId: z.uuid().optional(),
  /** Guardrail evaluation snapshots (present when step is in review or was blocked) */
  guardrailEvaluations: z
    .array(
      z.object({
        name: z.string(),
        kind: z.string(),
        stage: z.string(),
        action: z.string(),
        decision: z.string(),
        triggered: z.boolean(),
        reason: z.string(),
        source: z.string(),
        path: z.string().optional(),
        payloadPreview: z.string().optional(),
        metadata: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .optional(),
});

export {
  WorkflowExecutionDurationPattern,
  WorkflowExecutionDurationSchema,
  WorkflowStepRetryOverrideSchema,
  WorkflowStepExecutionOverrideSchema,
  WorkflowStepConfigExecutionOverrideSchema,
  type WorkflowStepExecutionOverride,
} from "@erp/workflows";

/**
 * List available step types response
 */
export const ListStepTypesResponseSchema = z.object({
  stepTypes: z.array(
    z.object({
      type: z.string(),
      name: z.string(),
      description: z.string(),
      category: StepCategorySchema,
      libraryModule: StepLibraryModuleSchema,
      inputSockets: z.array(SocketDefinitionSchema),
      outputSockets: z.array(SocketDefinitionSchema),
      // JSON Schema representation of the step's config schema
      configSchema: z.record(z.string(), z.unknown()).optional(),
      evalConfig: z
        .object({
          evalOutputKeys: z.array(z.string()),
          outputFormat: z.enum(["parallel_arrays", "object"]).optional(),
          contextOutputKeys: z.array(z.string()).optional(),
        })
        .optional(),
      deprecated: z
        .object({
          reason: z.string(),
          replacedBy: z.string().optional(),
        })
        .optional(),
      experimental: z
        .object({
          reason: z.string(),
        })
        .optional(),
      visibility: z
        .object({
          visibility: z.enum(["visible", "hidden", "restricted", "disabled"]),
          predicateResults: z
            .array(
              z.array(
                z.object({
                  type: z.string(),
                  passed: z.boolean(),
                  detail: z.string(),
                }),
              ),
            )
            .optional(),
          disabledReason: z.string().optional(),
        })
        .optional(),
    }),
  ),
});

export const ActionWorkflowInterfaceSchema = z.object({
  inputs: z.array(SocketDefinitionSchema),
  outputs: z.array(SocketDefinitionSchema),
});

export const ActionWorkflowOptionSchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
  name: z.string(),
  description: z.string().optional(),
  orchestratorType: OrchestratorTypeSchema,
  interface: ActionWorkflowInterfaceSchema,
});

export const ListActionWorkflowOptionsResponseSchema = z.object({
  workflows: z.array(ActionWorkflowOptionSchema),
});

/**
 * Socket definition for templates (uses permissive string type)
 */
export const TemplateSocketDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(), // More permissive than SocketDefinitionSchema
  required: z.boolean(),
  description: z.string().optional(),
});

/**
 * Workflow template step schema (simplified from StepDefinitionSchema)
 */
export const TemplateStepSchema = z.object({
  stepType: z.string(),
  name: z.string(),
  category: z.string(),
  config: z.record(z.string(), z.unknown()),
  position: z.object({ x: z.number(), y: z.number() }),
  sockets: z.object({
    inputs: z.array(TemplateSocketDefinitionSchema),
    outputs: z.array(TemplateSocketDefinitionSchema),
  }),
  emits: z.array(z.string()).optional(),
});

/**
 * Workflow template edge schema
 */
export const TemplateEdgeSchema = z.object({
  sourceStepIndex: z.number(),
  sourceSocketId: z.string(),
  targetStepIndex: z.number(),
  targetSocketId: z.string(),
});

/**
 * Workflow template response - full template structure
 */
export const WorkflowTemplateResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  version: z.number(),
  isActive: z.boolean(),
  tags: z.array(z.string()),
  orchestratorType: OrchestratorTypeSchema.optional(),
  isComponent: z.boolean(),
  steps: z.array(TemplateStepSchema),
  edges: z.array(TemplateEdgeSchema),
  retryConfig: z
    .object({
      enabled: z.boolean(),
      maxRetries: z.number(),
      initialDelay: z.number(),
      maxDelay: z.number(),
      backoffMultiplier: z.number(),
    })
    .optional(),
  exposedInputs: z.array(z.string()).optional(),
  exposedOutputs: z.array(z.string()).optional(),
  category: z
    .enum(["email", "ap", "po", "integration", "rebates", "custom"])
    .optional(),
  /**
   * Present when the template is a bundle: instantiating it installs several
   * workflows (and any supporting task types/pages) in one action. The
   * steps/edges above are a merged preview of every bundled workflow.
   */
  bundle: z
    .object({
      workflowNames: z.array(z.string()),
      taskTypeCount: z.number(),
      taskPageCount: z.number(),
    })
    .optional(),
});

/**
 * List workflow templates response
 */
export const ListWorkflowTemplatesResponseSchema = z.object({
  templates: z.array(WorkflowTemplateResponseSchema),
});

/**
 * Instantiate workflow template request
 */
export const InstantiateWorkflowTemplateRequestSchema = z.object({
  templateId: z.string(),
});

/**
 * Schedule info for cron-triggered workflows
 */
export const WorkflowScheduleInfoSchema = z.object({
  /**
   * The schedule_trigger step summarised here (the first one when the version
   * has several). Pass it as `triggerStepId` to `triggerScheduleNow`.
   */
  stepId: z.uuid().optional(),
  cronExpression: z.string(),
  timezone: z.string(),
  description: z.string().optional(),
});

/**
 * Event trigger info for event-driven workflows
 */
export const WorkflowEventTriggerInfoSchema = z.object({
  eventType: z.string(),
});

export const WorkflowGroupFrameSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

export const WorkflowGroupSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  workflowIds: z.array(z.uuid()),
  workflowCount: z.number(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const WorkflowGroupImplicitLinkSchema = z.object({
  sourceWorkflowId: z.uuid(),
  sourceStepId: z.uuid(),
  eventType: z.string(),
  targetWorkflowId: z.uuid(),
  targetStepId: z.uuid(),
});

/**
 * Group member shape — the version graph (steps/edges) plus the lineage-level
 * fields that the group UI needs for frame labels (name, isActive,
 * orchestratorType). We merge the two shapes here instead of nesting a
 * `workflow: WorkflowResponse` object so the frontend rendering code stays
 * flat — and so the OpenAPI surface keeps the fields at the top level where
 * external consumers expect them.
 */
export const WorkflowGroupMemberSchema =
  WorkflowDefinitionResponseSchema.extend({
    pinnedDefinitionId: z.uuid(),
    name: z.string(),
    description: z.string().nullable().optional(),
    /** @deprecated Always `[]`. */
    tags: DeprecatedWorkflowTagsSchema,
    isActive: z.boolean(),
    isComponent: z.boolean(),
    orchestratorType: OrchestratorTypeSchema,
    organizationId: z.uuid(),
  });

export const WorkflowGroupResponseSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  workflowIds: z.array(z.uuid()),
  workflowFrames: z.record(z.string(), WorkflowGroupFrameSchema),
  workflows: z.array(WorkflowGroupMemberSchema),
  implicitLinks: z.array(WorkflowGroupImplicitLinkSchema),
  missingWorkflowIds: z.array(z.uuid()),
  createdBy: z.uuid(),
  organizationId: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CreateWorkflowGroupRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  workflowIds: z.array(z.uuid()),
  workflowFrames: z.record(z.string(), WorkflowGroupFrameSchema).optional(),
});

export const UpdateWorkflowGroupRequestSchema = z.object({
  workflowGroupId: z.uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  workflowIds: z.array(z.uuid()).optional(),
  workflowFrames: z.record(z.string(), WorkflowGroupFrameSchema).optional(),
});

/**
 * Workflow summary — one row per Workflow for listing pages.
 *
 * The `id` is the Workflow row id (the lineage). `pinnedVersionId`
 * points at the canonical version used by triggers; `pinnedVersionNumber`
 * and `pinnedVersionChangelog` give the UI enough to show "v7 · Active"
 * without a second fetch.
 */
export const WorkflowDefinitionSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  // Matches `WorkflowResponseSchema.description` (nullable in the DB) —
  // otherwise a single workflow with `description: null` fails the whole
  // list-response parse and the workflows page 500s.
  description: z.string().nullable().optional(),
  /** @deprecated Always `[]`. */
  tags: DeprecatedWorkflowTagsSchema,
  isActive: z.boolean(),
  /**
   * Installed and re-asserted from code on every boot, so edits made in the UI
   * are overwritten on the next deploy. Excluded from the list unless
   * `systemManaged` is requested; the list badges the ones it does show.
   */
  isSystemManaged: z.boolean().optional(),
  /** Org namespace this workflow is grouped under (`namespaces.*`), if any. */
  namespaceId: z.uuid().nullable().optional(),
  orchestratorType: OrchestratorTypeSchema,
  createdBy: z.uuid(),
  createdByName: z.string().nullable().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable().optional(),
  nodeCount: z.number(),
  lastRun: WorkflowRunResponseSchema.optional(),
  scheduleInfo: WorkflowScheduleInfoSchema.optional(),
  eventTriggerInfo: WorkflowEventTriggerInfoSchema.optional(),
  // True when the pinned version has an `observe_data_source_link` (CDC)
  // trigger — signals that "Run Initial Ingest" is available for this workflow.
  hasObserveTrigger: z.boolean().optional(),
  // Pinned-version snapshot embedded for list display.
  pinnedVersionId: z.uuid().nullable().optional(),
  pinnedVersionNumber: z.number().optional(),
  pinnedVersionChangelog: z.string().nullable().optional(),
  draftCount: z.number().optional(),
  versionCount: z.number().optional(),
  /** Highest version number of any status; absent when there are no versions. */
  latestVersion: z.number().optional(),
  /**
   * Present only when the pinned version is a reusable action (it has an
   * `action_input` boundary). Counts what depends on it, so the list can warn
   * that editing or unpublishing this workflow reaches beyond itself. Detail for
   * one action lives in `listActionConsumers`; this is the count-only form,
   * since asking per row would mean a query per visible workflow.
   */
  actionUsage: z
    .object({
      /** Distinct `workflow_action` steps calling it, from other workflows. */
      workflowConsumerCount: z.number().int(),
      /** AR collections portal-action bindings on a customer profile. */
      portalActionBindingCount: z.number().int(),
    })
    .optional(),
  /**
   * Published agents that run this workflow as a setup phase, a teardown
   * phase, or an action tool. Omitted when none do.
   */
  agentUsage: z.array(AgentWorkflowUsageSchema).optional(),
});

/**
 * A step execution with bounded input/output previews (see getStepRun for the
 * full payloads).
 */
export const WorkflowRunDetailStepRunSchema = z.object({
  id: z.uuid().describe("Step run id; pass to workflows_getStepRun."),
  stepName: z.string().describe("Name of the step."),
  stepType: z.string().describe("Step type, e.g. ai_agent."),
  status: StepRunStatusSchema.describe("Step run status."),
  attemptNumber: z.number().describe("Attempt number, 1 for the first try."),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  duration: z.number().nullable().describe("Duration in milliseconds."),
  stopReason: z.string().nullable().describe("Error or stop reason."),
  inputPreview: z.string().describe("Input JSON truncated to 2000 characters."),
  outputPreview: z
    .string()
    .nullable()
    .describe("Output JSON truncated to 2000 characters, null if none."),
  hasGuardrailEvaluations: z
    .boolean()
    .describe("True when guardrail results exist; see workflows_getStepRun."),
});

export const GetWorkflowRunDetailsResponseSchema = z.object({
  run: z.object({
    id: z.uuid(),
    status: WorkflowRunStatusSchema,
    triggeredAt: z.iso.datetime(),
    startedAt: z.iso.datetime().nullable(),
    completedAt: z.iso.datetime().nullable(),
    totalSteps: z.number(),
    completedSteps: z.number(),
    stopReason: z.string().nullable(),
    errorStepId: z.uuid().nullable(),
    workflowId: z.uuid(),
  }),
  stepRuns: z.array(WorkflowRunDetailStepRunSchema),
  stepRunsTruncated: z
    .boolean()
    .describe("True when the run has more step runs than were returned."),
  fanoutItems: z
    .array(
      z.object({
        id: z.uuid().describe("Child workflow run id."),
        itemIndex: z.number().nullable().describe("Fanout item index."),
        status: WorkflowRunStatusSchema,
        totalSteps: z.number(),
        completedSteps: z.number(),
        stopReason: z.string().nullable(),
        stepRuns: z.array(WorkflowRunDetailStepRunSchema),
      }),
    )
    .optional()
    .describe("Fanout child runs, absent when the run did not fan out."),
  fanoutTotal: z
    .number()
    .describe("Child runs the run has in total (fanoutItems may be capped)."),
  childStepRunsTruncated: z
    .boolean()
    .describe(
      "True when the fanout children have more step runs than were returned (200 in total across children, oldest first).",
    ),
});

export const GetWorkflowStepRunResponseSchema = StepRunResponseSchema.extend({
  stepName: z.string().describe("Name of the step."),
  stepType: z.string().describe("Step type, e.g. ai_agent."),
});

/**
 * List workflow definition summaries request
 */
export const ListWorkflowDefinitionSummariesRequestSchema = z.object({
  limit: z.number().optional(),
  offset: z.number().optional(),
  includeDeleted: z.boolean().optional(),
  // Server-side filters (the workflows list paginates on the backend).
  search: z.string().optional(),
  mine: z.boolean().optional(),
  published: z.boolean().optional(),
  paused: z.boolean().optional(),
  scheduled: z.boolean().optional(),
  event: z.boolean().optional(),
  /**
   * Unlike the other quick filters, this one is not "no constraint when
   * omitted": code-owned workflows are EXCLUDED by default and this opts
   * into showing only them. They are not the user's to curate, so they
   * would otherwise be permanent noise in everyone's list.
   */
  systemManaged: z.boolean().optional(),
  /**
   * Match workflows assigned to any of these org namespaces. Combined
   * with `unassignedNamespace` as OR when both are set.
   */
  namespaceIds: z.array(z.uuid()).optional(),
  /** Match workflows with no namespace assignment. */
  unassignedNamespace: z.boolean().optional(),
  sortBy: z
    .enum(["name", "status", "createdAt", "updatedAt", "lastRun"])
    .optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
});

/**
 * List workflow definition summaries response
 */
export const ListWorkflowDefinitionSummariesResponseSchema = z.object({
  workflows: z.array(WorkflowDefinitionSummarySchema),
  total: z.number(),
  /** @deprecated Always `[]`. */
  availableTags: DeprecatedWorkflowTagsSchema,
});

/**
 * List runs for a workflow definition request
 */
export const WORKFLOW_RUN_LIST_SORT_FIELDS = [
  "triggeredAt",
  "status",
  "version",
] as const;
export const WorkflowRunListSortFieldSchema = z.enum(
  WORKFLOW_RUN_LIST_SORT_FIELDS,
);

export const ListWorkflowRunsForDefinitionRequestSchema = z.object({
  workflowDefinitionId: z
    .uuid()
    .describe(
      "Any version id of the workflow; runs of all its versions are listed.",
    ),
  limit: z
    .number()
    .int()
    .min(1)
    .max(250)
    .optional()
    .describe("Runs per page (default 50, max 250)."),
  offset: z.number().int().min(0).optional().describe("Runs to skip."),
  status: WorkflowRunStatusSchema.optional().describe(
    "Only runs in this status, e.g. failed.",
  ),
  /** Match any of these statuses. Combined (AND) with `status` when both are set. */
  statuses: z
    .array(WorkflowRunStatusSchema)
    .max(20)
    .optional()
    .describe("Match any of these statuses (AND with status when both set)."),
  triggeredByType: z
    .enum(["user", "system"])
    .optional()
    .describe("Only runs started by a user or by the system."),
  triggeredFrom: z.iso
    .datetime()
    .optional()
    .describe("Only runs triggered at or after this time."),
  triggeredTo: z.iso
    .datetime()
    .optional()
    .describe("Only runs triggered at or before this time."),
  search: z
    .string()
    .min(1)
    .max(255)
    .optional()
    .describe("Matches stop reason, or an exact run id."),
  testCaseId: z.uuid().optional().describe("Only runs of this test case."),
  /** Match runs of any of these test cases. */
  testCaseIds: z
    .array(z.uuid())
    .max(50)
    .optional()
    .describe("Only runs of any of these test cases."),
  // Pseudo-filter: restrict to scheduled (future) reruns, sorted by
  // scheduledFor ASC. Overrides `status`/`statuses` and the sort.
  onlyScheduledReruns: z
    .boolean()
    .optional()
    .describe(
      "Only future scheduled reruns, soonest first; overrides status and sort.",
    ),
  /** Defaults to `triggeredAt` DESC. */
  sortBy: WorkflowRunListSortFieldSchema.optional().describe(
    "Sort field (default triggeredAt).",
  ),
  sortDirection: z
    .enum(["asc", "desc"])
    .optional()
    .describe("Sort direction (default desc)."),
});

/**
 * List runs for a workflow definition response
 */
export const ListWorkflowRunsForDefinitionResponseSchema = z.object({
  runs: z.array(WorkflowRunResponseSchema),
  total: z.number(),
});

/**
 * List runs across every workflow in the org (the "All Runs" page).
 *
 * Same filter vocabulary as the per-workflow runs tab, plus a workflow picker
 * and an opt-in for fanout children (hidden by default so one fanned-out run
 * doesn't bury everything else).
 */
export const ListAllWorkflowRunsRequestSchema = z.object({
  limit: z.number().int().min(1).max(250).optional(),
  offset: z.number().int().min(0).optional(),
  /** Empty/omitted = every workflow in the org. */
  workflowIds: z.array(z.uuid()).max(50).optional(),
  status: WorkflowRunStatusSchema.optional(),
  triggeredByType: z.enum(["user", "system"]).optional(),
  triggeredFrom: z.iso.datetime().optional(),
  triggeredTo: z.iso.datetime().optional(),
  /** Matches stop reason, or an exact run id when the term parses as a UUID. */
  search: z.string().min(1).max(255).optional(),
  /** Include fanout/action child runs alongside top-level runs. */
  includeChildRuns: z.boolean().optional(),
  /**
   * Include runs of code-owned (`isSystemManaged`) workflows.
   *
   * Additive, unlike `systemManaged` on `listDefinitionSummaries` — there the
   * flag swaps the list over to system workflows only, here it widens an
   * otherwise-normal run list. Off by default: the Platform Heartbeat canary
   * alone beats every 5 minutes, so ~288 of its runs/day would bury a 50-row
   * page of the runs anyone is actually looking for.
   *
   * Ignored when `workflowIds` names specific workflows — picking a system
   * workflow explicitly is already the opt-in, and filtering it back out would
   * return an inexplicably empty list.
   */
  includeSystemManaged: z.boolean().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
});

export const ListAllWorkflowRunsResponseSchema = z.object({
  runs: z.array(WorkflowRunResponseSchema),
  total: z.number(),
});

/**
 * Live in-flight run counts for the All Runs page, grouped by workflow.
 *
 * Counts `running` rows (including fanout children) so a detached-fanout
 * parent that already closed does not hide work still executing. Independent
 * of the paginated list filters.
 */
export const RunningWorkflowRunStatSchema = z.object({
  workflowId: z.uuid(),
  workflowName: z.string(),
  runningCount: z.number().int().min(0),
});

export const GetRunningRunStatsResponseSchema = z.object({
  totalRunning: z.number().int().min(0),
  workflows: z.array(RunningWorkflowRunStatSchema),
});

/**
 * List rerun children of a workflow run
 */
export const ListRerunsForRunRequestSchema = z.object({
  workflowRunId: z.uuid(),
});

export const RerunSummarySchema = z.object({
  id: z.uuid(),
  status: WorkflowRunStatusSchema,
  triggeredAt: z.iso.datetime(),
  // Future fire time when the rerun is in "scheduled" status (else omitted).
  scheduledFor: z.iso.datetime().optional(),
  triggeredBy: z.uuid().nullable().optional(),
  triggeredByName: z.string().nullable().optional(),
  // Why a failed/cancelled rerun stopped, so a hover summary can show it.
  stopReason: z.string().optional(),
});

export const ListRerunsForRunResponseSchema = z.object({
  reruns: z.array(RerunSummarySchema),
});

/**
 * Linear snooze/rerun history that led to a run: original first, then each
 * snooze hop (`parentContext.snoozedFromWorkflowRunId`), then the current run.
 */
export const ListSnoozeLineageRequestSchema = z.object({
  workflowRunId: z.uuid(),
});

export const SnoozeLineageHopSchema = z.object({
  id: z.uuid(),
  status: WorkflowRunStatusSchema,
  triggeredAt: z.iso.datetime(),
  scheduledFor: z.iso.datetime().optional(),
  triggeredByName: z.string().nullable(),
  stopReason: z.string().optional(),
  snoozeReason: z.string().optional(),
  snoozedFromWorkflowRunId: z.uuid().optional(),
  rerunAttempt: z.number().int().positive().optional(),
  isCurrent: z.boolean(),
  isOriginal: z.boolean(),
});

export const ListSnoozeLineageResponseSchema = z.object({
  hops: z.array(SnoozeLineageHopSchema),
});

/**
 * Scheduled-rerun inbox: org-wide list of upcoming (future) scheduled reruns.
 */
export const ListUpcomingScheduledRerunsRequestSchema = z.object({
  limit: z.number().int().min(1).max(250).optional(),
  cursor: z.string().optional(),
});

export const UpcomingScheduledRerunSchema = z.object({
  id: z.uuid(),
  scheduledFor: z.iso.datetime(),
  rerunOfWorkflowRunId: z.uuid().nullable(),
  workflowId: z.uuid(),
  workflowDefinitionId: z.uuid(),
  workflowName: z.string().nullable(),
  triggeredBy: z.uuid().nullable(),
  triggeredByName: z.string().nullable(),
  snoozeReason: z.string().nullable(),
  entity: z.object({ type: z.string(), id: z.string() }).nullable(),
});

export const ListUpcomingScheduledRerunsResponseSchema = z.object({
  reruns: z.array(UpcomingScheduledRerunSchema),
  nextCursor: z.string().nullable(),
});

/**
 * Cancel a scheduled rerun before it fires (Temporal-only).
 */
export const CancelScheduledRerunRequestSchema = z.object({
  rerunWorkflowRunId: z.uuid(),
});

export const CancelScheduledRerunResponseSchema = z.object({
  workflowRun: WorkflowRunResponseSchema,
});

export const StopWorkflowRunsRequestSchema = z.object({
  workflowId: z.uuid(),
  /** Run states to stop across the entire workflow lineage. */
  statuses: z
    .array(z.enum(["running", "queued", "idle", "scheduled"]))
    .min(1)
    .max(4),
});

export const StopWorkflowRunsResponseSchema = z.object({
  /** Runs whose database state was changed to cancelled. */
  stopped: z.number().int(),
  /** Runs that finished or were claimed by another process before stopping. */
  alreadyResolved: z.number().int(),
  /** Runs whose Temporal cancellation could not be delivered. */
  failed: z.number().int(),
});

/**
 * Retry step request - retry a failed/timed_out step with optional input override
 */
export const RetryStepRequestSchema = z.object({
  workflowRunId: z.uuid(),
  stepRunId: z.uuid(),
  inputData: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Retry step response
 */
export const RetryStepResponseSchema = z.object({
  stepRun: StepRunResponseSchema,
  workflowRun: WorkflowRunResponseSchema,
});

/**
 * Restart from step request - restart workflow from a specific step
 */
export const RestartFromStepRequestSchema = z.object({
  workflowRunId: z.uuid(),
  workflowStepId: z.uuid(),
  inputData: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Restart from step response
 */
export const RestartFromStepResponseSchema = z.object({
  stepRun: StepRunResponseSchema,
  workflowRun: WorkflowRunResponseSchema,
  cancelledStepRunIds: z.array(z.uuid()),
});

/**
 * Resolve guardrail review request - confirm or dismiss a guardrail violation
 */
export const ResolveGuardrailReviewRequestSchema = z.object({
  workflowRunId: z.uuid(),
  stepRunId: z.uuid(),
  decision: z.enum(["confirmed", "dismissed"]),
  comment: z.string().optional(),
});

/**
 * Resolve guardrail review response
 */
export const ResolveGuardrailReviewResponseSchema = z.object({
  stepRun: StepRunResponseSchema,
  workflowRun: WorkflowRunResponseSchema,
});

/**
 * Get entity workflow context request
 */
export const GetEntityWorkflowContextRequestSchema = z.object({
  entityType: z.nativeEnum(SystemEntity),
  entityId: z.uuid(),
});

/**
 * Executed workflow step context for entity rerun dropdown
 */
export const EntityWorkflowExecutedStepSchema = z.object({
  workflowStepId: z.uuid(),
  stepName: z.string(),
  stepType: z.string(),
  latestStepRun: StepRunResponseSchema.nullable(),
});

/**
 * Minimal parent-run reference used to deep-link from an entity's workflow
 * context to the parent workflow run that spawned the current one. We only
 * carry the two ids the run-detail route needs (workflowId for the layout,
 * id for the run itself) — anything richer should go through getRun.
 */
export const EntityWorkflowParentRunSchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
});

/**
 * Get entity workflow context response
 */
export const GetEntityWorkflowContextResponseSchema = z.object({
  context: z
    .object({
      workflowRun: WorkflowRunResponseSchema,
      workflowDefinitionName: z.string(),
      steps: z.array(EntityWorkflowExecutedStepSchema),
      edges: z.array(EdgeDefinitionSchema),
      parentWorkflowRun: EntityWorkflowParentRunSchema.nullable(),
      // True when any run for this entity has a future scheduled rerun queued.
      hasUpcomingScheduledRerun: z.boolean().optional(),
    })
    .nullable(),
});

// ---------------------------------------------------------------------------
// Versioning endpoints
// See docs/projectplans/workflow-versioning.md for the full design.
// ---------------------------------------------------------------------------

const DiffStepSchema = z.object({
  key: z.string().optional(),
  stepType: z.string(),
  name: z.string(),
});
const DiffEndpointSchema = z.object({
  key: z.string(),
  name: z.string(),
  socketId: z.string(),
});
const DiffEdgeSchema = z.object({
  from: z.string(),
  to: z.string(),
  fromStep: DiffEndpointSchema.optional(),
  toStep: DiffEndpointSchema.optional(),
});

export const StructuredDiffSchema = z.object({
  addedSteps: z.array(DiffStepSchema),
  removedSteps: z.array(DiffStepSchema),
  modifiedSteps: z.array(
    DiffStepSchema.extend({ configChanges: z.array(z.string()) }),
  ),
  addedEdges: z.array(DiffEdgeSchema),
  removedEdges: z.array(DiffEdgeSchema),
});

export const WorkflowEditTrailSchema = z
  .array(z.object({ label: z.string(), at: z.number() }))
  .transform(normalizeWorkflowEditTrail)
  .catch([]);

export const DraftFreshnessSchema = z.object({
  parentVersionId: z.uuid().nullable(),
  parentVersionNumber: z.number().nullable(),
  pinnedVersionId: z.uuid().nullable(),
  pinnedVersionNumber: z.number().nullable(),
  latestFinalizedVersionNumber: z.number().nullable(),
  newerFinalizedVersionNumbers: z.array(z.number()),
  isBehindPinned: z.boolean(),
  lastEditedAt: z.iso.datetime(),
  idleDays: z.number(),
  suggestRestart: z.boolean(),
});

export const PreviewVersionDiffRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
});

export const PreviewVersionDiffResponseSchema = z.object({
  workflowName: z.string(),
  draftVersionNumber: z.number(),
  parentVersionNumber: z.number().nullable(),
  pinnedVersionNumber: z.number().nullable(),
  isBehindPinned: z.boolean(),
  yourChanges: StructuredDiffSchema,
  liveChangesSinceFork: StructuredDiffSchema.nullable(),
  netEffectOnLive: StructuredDiffSchema.nullable(),
});

export const VersionJsonDiffRequestSchema = z.object({
  fromVersionId: z.uuid(),
  toVersionId: z.uuid(),
});

export const WorkflowCanonicalJsonSchema = z.record(
  z.string(),
  JsonWireValueSchema,
);
export const VersionJsonDiffResponseSchema = z.object({
  from: WorkflowCanonicalJsonSchema,
  to: WorkflowCanonicalJsonSchema,
});

/**
 * Summary of a single version in a lineage — used by the version-history UI.
 * Includes author info so the UI can render "v3 by Alice on 2026-04-20".
 */
export const WorkflowVersionSummarySchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
  parentVersionId: z.uuid().nullable().optional(),
  version: z.number(),
  status: WorkflowDefinitionStatusSchema,
  // Derived at mapping time from `workflow.pinnedVersionId === id`.
  isPinned: z.boolean(),
  // Derived from run history: the version's most recent terminal root run
  // failed zod validation (the signal that triggers automatic LKG rollback).
  failingValidation: z.boolean().optional(),
  changelog: z.string().nullable().optional(),
  createdBy: z.uuid(),
  createdByName: z.string().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  freshness: DraftFreshnessSchema.optional(),
});

export const CreateDraftRequestSchema = z.object({
  workflowId: z.uuid(),
  /**
   * Version to fork the draft from. If omitted, defaults to the workflow's
   * currently-pinned version. Passing a historical version creates a draft
   * whose parent is that historical version — useful for "I want to branch
   * off an old version to bring back its behavior".
   */
  sourceVersionId: z.uuid().optional(),
  onExistingDraft: z.enum(["resume", "replace"]).optional(),
});

export const CreateDraftResponseSchema = z.object({
  definition: WorkflowDefinitionResponseSchema,
  /**
   * If true, the user already had a draft in this lineage and we returned it
   * instead of creating a new one. Prevents multiple drafts per user per lineage.
   */
  resumed: z.boolean(),
  freshness: DraftFreshnessSchema.optional(),
  requestedSourceIgnored: z.boolean().optional(),
});

export const FinalizeVersionRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  /**
   * If the draft's parent version is no longer the pinned version, finalize
   * returns a stale-base warning instead of performing the action. Re-post
   * with `acknowledgeStaleBase: true` to proceed anyway.
   */
  acknowledgeStaleBase: z.boolean().optional(),
  confirmWorkflowName: z.string().optional(),
  editTrail: WorkflowEditTrailSchema.optional(),
});

export const StaleBaseWarningSchema = z.object({
  kind: z.literal("stale_base"),
  workflowName: z.string(),
  draftParentVersionId: z.uuid().nullable(),
  draftParentVersionNumber: z.number().nullable(),
  currentPinnedVersionId: z.uuid().nullable(),
  currentPinnedVersionNumber: z.number().nullable(),
  message: z.string(),
});

export const FinalizeVersionResponseSchema = z.discriminatedUnion("ok", [
  z.object({
    ok: z.literal(true),
    definition: WorkflowDefinitionResponseSchema,
    warnings: WorkflowValidationWarningsSchema,
  }),
  z.object({ ok: z.literal(false), warning: StaleBaseWarningSchema }),
]);

export const PinVersionRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  acknowledgeStaleBase: z.boolean().optional(),
  confirmWorkflowName: z.string().optional(),
});

/** Same outcome shape as finalizing: the version, or a stale-base warning. */
export const PinVersionResponseSchema = FinalizeVersionResponseSchema;

export const UpdateVersionChangelogRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  changelog: z.string().max(MAX_CHANGELOG_CHARS),
});

export const ListVersionsRequestSchema = z.object({
  workflowId: z.uuid().describe("Workflow (lineage) id to list versions of"),
});

export const ListVersionsResponseSchema = z.object({
  versions: z.array(WorkflowVersionSummarySchema),
});

// ── Activity timeline (audit trail) ──────────────────────────────────────────

export const WorkflowActivityKindSchema = z.enum([
  "workflow_created",
  "workflow_updated",
  "workflow_activated",
  "workflow_paused",
  "workflow_deleted",
  "workflow_restored",
  "version_created",
  "version_finalized",
  "version_pinned",
  "version_updated",
  "version_deleted",
  "version_restored",
  "graph_edited",
]);

export const WorkflowActivityActorSchema = ActivityActorSchema;

/** A version referenced by an entry. `version` is null when the row is gone. */
export const WorkflowActivityVersionRefSchema = z.object({
  id: z.uuid(),
  version: z.number().int().nullable(),
});

export const WorkflowActivityFieldDiffSchema = ActivityFieldDiffSchema;

export const WorkflowActivityGraphChangesSchema = z.object({
  stepsAdded: z.number().int(),
  stepsUpdated: z.number().int(),
  stepsRemoved: z.number().int(),
});

export const WorkflowActivityEntrySchema = z.object({
  id: z.string(),
  kind: WorkflowActivityKindSchema,
  timestamp: z.iso.datetime(),
  actor: WorkflowActivityActorSchema,
  /** The version the entry is about; null for lineage-level events. */
  version: WorkflowActivityVersionRefSchema.nullable(),
  /** `version_created`: the version the new one was forked from. */
  parentVersion: WorkflowActivityVersionRefSchema.nullable().optional(),
  /** `version_pinned`: the version that was published before this change. */
  previousVersion: WorkflowActivityVersionRefSchema.nullable().optional(),
  /**
   * `version_pinned` by the system: the root run whose validation failure
   * triggered the automatic rollback to last known good.
   */
  rollbackRunId: z.uuid().nullable().optional(),
  /** `workflow_updated` / `version_updated`: column → { old, new }. */
  changedFields: z
    .record(z.string(), WorkflowActivityFieldDiffSchema)
    .optional(),
  /** `graph_edited`: how many steps the save touched. */
  graphChanges: WorkflowActivityGraphChangesSchema.optional(),
});

export const ListWorkflowActivityRequestSchema = z.object({
  workflowId: z
    .uuid()
    .describe("Workflow (lineage) id whose audit trail to list"),
  ...activityPageInputShape,
});

export const ListWorkflowActivityResponseSchema = activityPageResponseSchema(
  WorkflowActivityEntrySchema,
);

export const ListDraftsRequestSchema = z.object({
  /**
   * If true (default), list drafts owned by the calling user. If false, list
   * all drafts in the organization.
   */
  ownedByCurrentUser: z.boolean().optional(),
});

export const ListDraftsResponseSchema = z.object({
  drafts: z.array(WorkflowVersionSummarySchema),
});

export const DeleteDraftRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
});

export const DeleteDraftResponseSchema = z.object({
  success: z.literal(true),
  /** True when the draft was the lineage's only version, so the whole workflow
   *  was deleted with it — a versionless workflow can't be opened or branched
   *  from, so it must not survive its last draft. */
  workflowDeleted: z.boolean(),
});

export const RegenerateChangelogRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
});

export const ListPendingApprovalsRequestSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
});

/**
 * Fields every order rollup carries, whatever the subject. Spread into each
 * variant rather than shared via `.extend()` so `z.discriminatedUnion` sees
 * plain object schemas and can narrow on `kind`.
 */
const approvalOrderSummaryCommonFields = {
  orderNumber: z.string().nullable(),
  totalAmount: z.number().nullable(),
  currency: z.string().nullable(),
  lineCount: z.number().int(),
};

/** Rollup of the draft sales order behind an approval. */
export const ApprovalSalesOrderSummarySchema = z.object({
  kind: z.literal("sales_order"),
  draftSalesOrderId: z.string().uuid(),
  customerName: z.string().nullable(),
  /** Weighted list-vs-sold discount %, null when no list prices resolve. */
  discountPct: z.number().nullable(),
  ...approvalOrderSummaryCommonFields,
});

/** Rollup of the draft purchase order behind an approval. */
export const ApprovalPurchaseOrderSummarySchema = z.object({
  kind: z.literal("purchase_order"),
  draftPurchaseOrderId: z.string().uuid(),
  supplierName: z.string().nullable(),
  /** Ship-to warehouse, shown where a sales-order row shows the discount. */
  warehouseName: z.string().nullable(),
  ...approvalOrderSummaryCommonFields,
});

/**
 * Rollup of the discount group behind an approval. Its own shape — a group
 * has no order number / money total, so the common order fields don't apply.
 */
export const ApprovalDiscountGroupSummarySchema = z.object({
  kind: z.literal("discount_group"),
  discountGroupId: z.string().uuid(),
  groupName: z.string(),
  customerName: z.string().nullable(),
  referenceCode: z.string().nullable(),
  elementCount: z.number().int(),
});

/**
 * Humanized rollup of the order behind an approval, so inbox rows can read
 * "Order V075-26 · MARIN & MASKIN — $30,063.46 · 11 items" (sales orders) or
 * "PO 4500123 · ACME SUPPLY — $42,300 · 8 lines" (procurement draft POs)
 * without a per-row draft fetch. Null for approvals with neither subject.
 *
 * A union, not one flat object with optional halves: each variant's own id
 * (`draftSalesOrderId` / `draftPurchaseOrderId`) stays required within its
 * branch, and a PO rollup cannot carry sales-order-only fields like
 * `customerName` or `discountPct`.
 */
export const ApprovalOrderSummarySchema = z.discriminatedUnion("kind", [
  ApprovalSalesOrderSummarySchema,
  ApprovalPurchaseOrderSummarySchema,
  ApprovalDiscountGroupSummarySchema,
]);

export const PendingApprovalItemSchema = z.object({
  workflowRunId: z.string().uuid(),
  workflowId: z.string().uuid(),
  workflowName: z.string(),
  workflowDefinitionId: z.string().uuid(),
  triggeredAt: z.iso.datetime(),
  triggeredByName: z.string().nullable(),
  stepRunId: z.string().uuid(),
  stepName: z.string(),
  instructions: z.string().nullable(),
  reviewData: z.record(z.string(), z.unknown()).nullable(),
  orderSummary: ApprovalOrderSummarySchema.nullable().optional(),
  // The gate's named escalation paths (empty when it has none) — the inbox only
  // shows an "Escalate" action when this is non-empty.
  escalationPaths: z
    .array(z.object({ name: z.string(), label: z.string().nullable() }))
    .default([]),
});

export type ApprovalOrderSummary = z.infer<typeof ApprovalOrderSummarySchema>;
export type ApprovalSalesOrderSummary = z.infer<
  typeof ApprovalSalesOrderSummarySchema
>;
export type ApprovalPurchaseOrderSummary = z.infer<
  typeof ApprovalPurchaseOrderSummarySchema
>;
export type ApprovalDiscountGroupSummary = z.infer<
  typeof ApprovalDiscountGroupSummarySchema
>;

export const ListPendingApprovalsResponseSchema = z.object({
  approvals: z.array(PendingApprovalItemSchema),
  total: z.number(),
});

export type PendingApprovalItem = z.infer<typeof PendingApprovalItemSchema>;
export type ListPendingApprovalsResponse = z.infer<
  typeof ListPendingApprovalsResponseSchema
>;

// ── Automation Score ─────────────────────────────────────────────────

export const GetAutomationScoreRequestSchema = z.object({
  workflowId: z.uuid(),
  workflowDefinitionId: z.uuid().optional(),
});

export const ApprovalStepBreakdownItemSchema = z.object({
  stepName: z.string(),
  totalRuns: z.number().int(),
  skippedRuns: z.number().int(),
  completedRuns: z.number().int(),
  skipRate: z.number(),
});

export const VersionScoreItemSchema = z.object({
  version: z.number().int(),
  totalRuns: z.number().int(),
  automatedRuns: z.number().int(),
  score: z.number().nullable(),
});

export const WeeklyScoreItemSchema = z.object({
  weekStart: z.string(),
  totalRuns: z.number().int(),
  automatedRuns: z.number().int(),
  score: z.number().nullable(),
});

export const GetAutomationScoreResponseSchema = z.object({
  totalCompletedRuns: z.number().int(),
  automatedRuns: z.number().int(),
  score: z.number().nullable(),
  sampleSufficient: z.boolean(),
  hasApprovalSteps: z.boolean(),
  approvalStepBreakdown: z.array(ApprovalStepBreakdownItemSchema),
  versionHistory: z.array(VersionScoreItemSchema),
  avgApprovalDurationMs: z.number().nullable(),
  last7Days: z.object({
    totalCompletedRuns: z.number().int(),
    automatedRuns: z.number().int(),
    score: z.number().nullable(),
  }),
  weeklyHistory: z.array(WeeklyScoreItemSchema),
});

export type GetAutomationScoreRequest = z.infer<
  typeof GetAutomationScoreRequestSchema
>;
export type GetAutomationScoreResponse = z.infer<
  typeof GetAutomationScoreResponseSchema
>;
export type ApprovalStepBreakdownItem = z.infer<
  typeof ApprovalStepBreakdownItemSchema
>;
export type VersionScoreItem = z.infer<typeof VersionScoreItemSchema>;

// ---------------------------------------------------------------------------
// Authoring catalog + lookups + version comparison
// (shared with the chat agent's workflow discovery tools)
// ---------------------------------------------------------------------------

export const WorkflowEngineHelpTopicSchema = z.enum([
  "sockets",
  "readiness",
  "fanout",
  "triggers",
  "static_inputs",
  "dynamic_sockets",
  "email_pattern",
  "code_sandbox",
  "voice_agent_extraction",
]);
export type WorkflowEngineHelpTopic = z.infer<
  typeof WorkflowEngineHelpTopicSchema
>;

const StepTypeSummarySchema = z.object({
  type: z.string().describe('Step type identifier, e.g. "event_trigger"'),
  name: z.string().describe("Human-readable step name"),
  category: StepCategorySchema.describe("Step category"),
  description: z.string().describe("What the step does"),
});

export const SearchStepTypesRequestSchema = z.object({
  search: z
    .string()
    .optional()
    .describe(
      "Text matched (case-insensitive) against type, name, description",
    ),
  category: StepCategorySchema.optional().describe("Only this step category"),
  limit: z
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .describe("Maximum step types to return; defaults to 50"),
  workflowDefinitionId: z
    .uuid()
    .optional()
    .describe(
      "Workflow definition whose context scopes step visibility (optional)",
    ),
});
export const SearchStepTypesResponseSchema = z.object({
  steps: z
    .array(StepTypeSummarySchema)
    .describe("Visible, non-deprecated step types matching the filters"),
  truncated: z
    .boolean()
    .describe("True when more matches existed than `limit` allowed"),
});

export const InspectStepTypeRequestSchema = z.object({
  type: z.string().describe('The step type identifier, e.g. "event_trigger"'),
  workflowDefinitionId: z
    .uuid()
    .optional()
    .describe(
      "Workflow definition whose context scopes step visibility (optional)",
    ),
});
export const InspectStepTypeResponseSchema = StepTypeSummarySchema.extend({
  inputSockets: z
    .array(SocketDefinitionSchema)
    .describe("Input sockets derived from the step's input schema"),
  outputSockets: z
    .array(SocketDefinitionSchema)
    .describe("Output sockets derived from the step's output schema"),
  configSchema: z
    .record(z.string(), z.unknown())
    .optional()
    .describe("JSON Schema of the step's config, when serializable"),
});

export const GetWorkflowEngineHelpRequestSchema = z.object({
  topic: WorkflowEngineHelpTopicSchema.describe("Engine concept to explain"),
});
export const GetWorkflowEngineHelpResponseSchema = z.object({
  topic: WorkflowEngineHelpTopicSchema.describe("The topic that was explained"),
  guidance: z.string().describe("Detailed guidance for the topic"),
});

export const ListCoreEnumsLookupRequestSchema = z.object({
  nameFilter: z
    .string()
    .optional()
    .describe("Substring to filter enum names (case-insensitive)"),
  limit: z
    .number()
    .int()
    .min(1)
    .max(200)
    .optional()
    .describe("Maximum enums to return; defaults to 50"),
});
export const ListCoreEnumsLookupResponseSchema = z.object({
  enums: z
    .array(
      z.object({
        id: z.uuid().describe("Core enum id (use as coreEnumId)"),
        name: z.string().describe("Enum name"),
        description: z.string().describe("What the enum represents"),
        options: z
          .array(
            z.object({
              id: z.uuid().describe("Option id"),
              name: z.string().nullable().describe("Option label"),
              sortOrder: z.number().describe("Display order"),
            }),
          )
          .describe("The enum's options"),
      }),
    )
    .describe("Core enums ordered by name, with their options"),
  truncated: z
    .boolean()
    .describe("True when more enums matched than `limit` allowed"),
});

export const ListSystemEventsLookupResponseSchema = z.object({
  events: z
    .array(
      z.object({
        eventType: z
          .string()
          .describe("System event type (use as event_trigger eventType)"),
        fields: z
          .array(
            z.object({
              name: z.string().describe("Payload field name"),
              type: z.string().describe("Payload field type"),
              description: z.string().optional(),
            }),
          )
          .describe("Payload fields the event carries"),
      }),
    )
    .describe("System events usable with event_trigger steps"),
});

export const CompareWorkflowVersionsRequestSchema = z.object({
  baseVersionId: z
    .uuid()
    .describe(
      "Older version's definition id (from listVersions `id`), not the version number",
    ),
  targetVersionId: z
    .uuid()
    .describe(
      "Newer version's definition id (from listVersions `id`), not the version number",
    ),
  workflowId: z
    .uuid()
    .optional()
    .describe("When set, both versions must belong to this workflow"),
});
export const CompareWorkflowVersionsResponseSchema = StructuredDiffSchema;

/**
 * Workflow contracts
 */
export const workflowContracts = {
  // Create a workflow (also creates its first definition/version in the
  // same transaction and pins it).
  createDefinition: oc
    .input(CreateWorkflowDefinitionRequestSchema)
    .output(SaveWorkflowDefinitionResponseSchema)
    .route({
      summary: "Create a workflow",
      description:
        "Creates a workflow with name, description, steps, edges and namespace, plus its first version, which is pinned; validates the graph unless skipped.",
      method: "POST",
    }),

  // Update version-level content on a specific definition row.
  // Lineage-level edits go through updateWorkflow.
  updateDefinition: oc
    .input(UpdateWorkflowDefinitionRequestSchema)
    .output(SaveWorkflowDefinitionResponseSchema)
    .route({
      summary: "Update a workflow version's steps",
      description:
        "Saves the steps, edges and layout of one workflow version (typically a draft); name and activation are changed via the workflow update instead.",
      method: "PUT",
    }),

  // Validate a draft's graph without persisting or publishing. Returns all
  // errors (the publish path throws on the first).
  validateDefinition: oc
    .input(ValidateWorkflowDefinitionRequestSchema)
    .output(ValidateWorkflowDefinitionResponseSchema)
    .route({
      summary: "Validate a workflow graph",
      description:
        "Checks a draft workflow's steps and edges and returns every validation error, without saving or publishing anything.",
      method: "POST",
    }),

  // Get a specific version (WorkflowDefinition row) by ID.
  getDefinition: oc
    .input(
      z.object({
        workflowDefinitionId: z
          .uuid()
          .describe("Workflow definition (version) id to fetch"),
      }),
    )
    .output(WorkflowDefinitionResponseSchema)
    .route({
      summary: "Get a workflow version",
      description:
        "Returns one workflow version with its steps, edges, status and version number.",
      method: "GET",
    }),

  // Get the Workflow (lineage-level entity) by ID.
  getWorkflow: oc
    .input(z.object({ workflowId: z.uuid() }))
    .output(WorkflowResponseSchema)
    .route({
      summary: "Get a workflow",
      description:
        "Returns a workflow's workflow-level settings: name, description, active state, pinned version and namespace.",
      method: "GET",
    }),

  /**
   * Reverse lookup for a reusable action: every `workflow_action` step, in any
   * version of any workflow in the org, that calls into *any* version of this
   * action's lineage — plus the consumers that live outside the workflow engine
   * (today: AR collections portal-action bindings on a customer profile).
   * Powers the "used by" linkback on `action_input` nodes, where the question is
   * "who depends on this action?" — a question that is version-agnostic by
   * nature, since consumers pin whichever version they were built against.
   */
  listActionConsumers: oc
    .input(z.object({ workflowId: z.uuid() }))
    .output(
      z.object({
        /**
         * Consumers reached from a customer profile's "Agent portal actions"
         * editor rather than from another workflow's graph. Same question, other
         * side of the app — an action bound here breaks a collections flow if
         * it's changed or unpublished, so it belongs in the same linkback.
         */
        portalActionBindings: z.array(
          z.object({
            customerId: z.uuid(),
            customerName: z.string(),
            /** The binding's id inside the customer's collections config. */
            bindingId: z.string(),
            /** Label the binding shows on the thread's action button. */
            label: z.string(),
            /**
             * Which version of this action the binding is pinned to, or null
             * when the pinned definition is no longer in the lineage.
             */
            version: z.number().int().nullable(),
            /** True when the binding runs unattended at thread creation. */
            runOnThreadCreation: z.boolean(),
          }),
        ),
        consumers: z.array(
          z.object({
            /** The consuming workflow lineage. */
            workflowId: z.uuid(),
            workflowName: z.string(),
            isActive: z.boolean(),
            /** The consuming *version* that holds the workflow_action step. */
            workflowDefinitionId: z.uuid(),
            version: z.number().int(),
            status: WorkflowDefinitionStatusSchema,
            /** True when this is the consumer's canonical (pinned) version. */
            isPinnedVersion: z.boolean(),
            /** The workflow_action step doing the calling. */
            stepId: z.uuid(),
            stepName: z.string(),
            /**
             * Which version of this action that step is wired to — the pinned
             * version for "latest published" references, or null when nothing
             * is published yet.
             */
            calledVersion: z.number().int().nullable(),
          }),
        ),
      }),
    )
    .route({
      summary: "List what uses a reusable workflow action",
      description:
        "Returns every workflow step, in any version, that calls this reusable action workflow, plus customer collections portal actions bound to it.",
      method: "GET",
    }),

  // Update workflow-level fields (name, isActive, orchestratorType,
  // etc.) — applies immediately, affects every version.
  updateWorkflow: oc
    .input(UpdateWorkflowRequestSchema)
    .output(WorkflowResponseSchema)
    .route({
      summary: "Update workflow settings",
      description:
        "Changes workflow-level settings across all versions: name, description, active or paused, component inputs/outputs, orchestrator, fanout, retries, manager agent or namespace.",
      method: "PATCH",
    }),

  // List workflow definitions
  listDefinitions: oc
    .input(
      z.object({
        limit: z.number().optional(),
        offset: z.number().optional(),
      }),
    )
    .output(
      z.object({
        workflows: z.array(WorkflowDefinitionResponseSchema),
        total: z.number(),
      }),
    )
    .route({
      summary: "List workflow versions",
      description:
        "Paginated list of workflow versions in the organization with their steps and edges, plus a total count.",
      method: "GET",
    }),

  // Trigger a workflow
  trigger: oc
    .input(TriggerWorkflowRequestSchema)
    .output(WorkflowRunResponseSchema)
    .route({
      summary: "Start a workflow run",
      description:
        "Starts a run of a workflow version with optional initial inputs and trigger step; can mark it as a rerun of an earlier run. Returns the new run.",
      method: "POST",
    }),

  // Trigger a scheduled workflow immediately (manual run for testing).
  // `triggerStepId` picks the schedule_trigger when the version has several;
  // optional when it has exactly one.
  triggerScheduleNow: oc
    .input(
      z.object({
        workflowDefinitionId: z.uuid(),
        triggerStepId: z.uuid().optional(),
      }),
    )
    .output(
      z.object({
        success: z.boolean(),
        message: z.string(),
        workflowRunId: z.uuid().optional(),
      }),
    )
    .route({
      summary: "Run a scheduled workflow now",
      description:
        "Starts a scheduled workflow immediately as a manual run, choosing the schedule trigger when the version has several; returns the run ID.",
      method: "POST",
    }),

  // Check whether a form-trigger slug is available (case-insensitive, across
  // all organizations — form URLs are global). `workflowId` excludes the
  // caller's own lineage so re-saving an unchanged slug stays "available".
  checkFormSlug: oc
    .input(
      z.object({
        slug: z.string().min(1).max(200),
        workflowId: z.uuid().optional(),
      }),
    )
    .output(
      z.object({
        available: z.boolean(),
        normalizedSlug: z.string(),
        reason: z.enum(["invalid_format", "taken"]).optional(),
      }),
    )
    .route({
      summary: "Check if a form URL slug is available",
      description:
        "Checks whether a workflow form's URL slug is valid and unused across all organizations, ignoring the caller's own workflow.",
      method: "POST",
    }),

  /**
   * Airtable-style form submissions grid for a workflow lineage.
   *
   * Columns come from the pinned (or requested) `form_trigger` field config;
   * each row is a workflow run with the form_trigger StepRun's field values
   * plus run status / submitter metadata.
   */
  listFormSubmissions: oc
    .input(
      z.object({
        workflowDefinitionId: z.uuid(),
        limit: z.number().int().min(1).max(250).optional(),
        offset: z.number().int().min(0).optional(),
      }),
    )
    .output(
      z.object({
        slug: z.string().nullable(),
        title: z.string(),
        fields: z.array(
          z.object({
            id: z.string(),
            label: z.string(),
            type: z.enum(FORM_FIELD_TYPES),
          }),
        ),
        submissions: z.array(
          z.object({
            runId: z.uuid(),
            status: WorkflowRunStatusSchema,
            submittedAt: z.iso.datetime(),
            submittedBy: z.string(),
            workflowDefinitionVersion: z.number().optional(),
            /** Field-id → raw form_trigger output value (missing = empty cell). */
            values: z.record(z.string(), z.unknown()),
          }),
        ),
        total: z.number().int().nonnegative(),
      }),
    )
    .route({
      summary: "List form submissions for a workflow",
      description:
        "Returns a grid of form-triggered runs of a workflow: form fields as columns, each submission's values, submitter, time and run status, paginated.",
      method: "GET",
    }),

  // Run the Observe DataSource Link initial ingest now (manual equivalent of the
  // publish-time initial run). Starts one phase:"initial" run per observed link.
  triggerObserveInitialIngest: oc
    .input(
      z.object({
        workflowDefinitionId: z.uuid(),
        /**
         * Narrow the ingest to a single observed DataSource Link. Omitted, every
         * link the trigger resolves is re-baselined — which for a
         * `matching_schema` selection is every table of the schema. Pass a link
         * id to recover one division without touching its siblings.
         */
        dataSourceLinkId: z.uuid().optional(),
      }),
    )
    .output(
      z.object({
        success: z.boolean(),
        message: z.string(),
        workflowRunIds: z.array(z.uuid()),
      }),
    )
    .route({
      summary: "Run initial ingest for observed data tables",
      description:
        "Starts initial-ingest runs that re-baseline every data table a workflow's observe step watches, or just one; returns the started run IDs.",
      method: "POST",
    }),

  // List the DataSource Links a definition's `observe_data_source_link` steps
  // resolve to. Powers the per-link "Run Initial Ingest" menu: a
  // `matching_schema` selection fans out to every table of the schema, so the
  // client cannot derive the concrete links itself. Empty when CDC is
  // unconfigured or the definition has no observe step.
  listObserveDataSourceLinks: oc
    .input(z.object({ workflowDefinitionId: z.uuid() }))
    .output(
      z.object({
        links: z.array(
          z.object({
            id: z.uuid(),
            dataSourceId: z.uuid(),
            schemaName: z.string(),
            tableName: z.string(),
            dataSourceTitle: z.string().nullable(),
          }),
        ),
      }),
    )
    .route({
      summary: "List data tables a workflow observes",
      description:
        "Returns the data source tables a workflow version's observe steps resolve to, with schema and table names; empty if none.",
      method: "GET",
    }),

  // Get workflow run status
  getRun: oc
    .input(GetWorkflowRunRequestSchema)
    .output(WorkflowRunResponseSchema)
    .route({
      summary: "Get a workflow run",
      description:
        "Returns one workflow run with status, trigger details, timing, stop reason and rerun links.",
      method: "GET",
    }),

  // Get step runs for a workflow run
  getStepRuns: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(
      z.object({
        stepRuns: z.array(StepRunResponseSchema),
      }),
    )
    .route({
      summary: "List step runs of a workflow run",
      description:
        "Returns every step execution in a workflow run with status, inputs, outputs, errors and timing.",
      method: "GET",
    }),

  // One run with step runs (bounded previews) and fanout children
  getRunDetails: oc
    .input(
      z.object({
        workflowRunId: z
          .uuid()
          .describe("Workflow run id (from workflows_listRunsForDefinition)."),
      }),
    )
    .output(GetWorkflowRunDetailsResponseSchema)
    .route({
      summary: "Get a workflow run with step runs",
      description:
        "Returns one run with its first 200 step runs (input and output truncated to previews) and up to 50 fanout child runs holding at most 200 step runs in total, for debugging a failure. Truncation flags show when anything was cut.",
      method: "GET",
    }),

  // One step run with full hydrated payloads
  getStepRun: oc
    .input(z.object({ stepRunId: z.uuid().describe("Step run id.") }))
    .output(GetWorkflowStepRunResponseSchema)
    .route({
      summary: "Get one step run",
      description:
        "Returns a single step run with its full input and output payloads, stop reason and guardrail evaluations.",
      method: "GET",
    }),

  // Get agent tool-call StepRuns recorded against this workflow run (e.g.
  // computer-use operator iterations from a remote-desktop step). These carry
  // a null workflowStepId and so are excluded from getStepRuns; the trace nests
  // them under their `parentStepRunId` step.
  getWorkflowRunToolCalls: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(
      z.object({
        toolCalls: z.array(ToolCallRunResponseSchema),
      }),
    )
    .route({
      summary: "List agent tool calls in a workflow run",
      description:
        "Returns agent tool-call executions recorded in a workflow run, such as computer-use operator iterations, nested under their parent step.",
      method: "GET",
    }),

  // Get logs for a step run
  getStepRunLogs: oc
    .input(z.object({ stepRunId: z.uuid() }))
    .output(
      z.object({
        logs: z.array(
          z
            .object({
              level: z.number(),
              time: z.string(),
              msg: z.string(),
              workflowRunId: z.string().optional(),
              stepRunId: z.string().optional(),
              stepType: z.string().optional(),
              stepName: z.string().optional(),
            })
            .passthrough(),
        ),
      }),
    )
    .route({
      summary: "Get logs for a workflow step run",
      description:
        "Returns the log lines recorded for one step execution with level, time and message.",
      method: "GET",
    }),

  // Sandbox telemetry for a `code_sandbox` step run — the nested in-sandbox
  // trace + lifetime metrics rollup. Served from our materialized tables (no
  // live Daytona call). `sandbox` is null for non-sandbox steps; `pending` is
  // true until the background ingest has pulled telemetry (`materialized_at`).
  getStepRunSandbox: oc
    .input(z.object({ stepRunId: z.uuid() }))
    .output(
      z.object({
        sandbox: sandboxInstanceSchema.nullable(),
        spans: z.array(sandboxSpanSchema),
      }),
    )
    .route({
      summary: "Get code sandbox trace for a step run",
      description:
        "Returns the recorded sandbox instance, metrics and trace spans for a code-sandbox step execution; pending until telemetry is collected.",
      method: "GET",
    }),

  // Sandbox stdout/stderr/system logs for a `code_sandbox` step run.
  getStepRunSandboxLogs: oc
    .input(z.object({ stepRunId: z.uuid() }))
    .output(
      z.object({
        lines: z.array(sandboxLogLineSchema),
        truncated: z.boolean(),
      }),
    )
    .route({
      summary: "Get code sandbox logs for a step run",
      description:
        "Returns standard output, error and system log lines from a code-sandbox step execution, flagging whether they were truncated.",
      method: "GET",
    }),

  // Get unified logs for all steps in a workflow run (includes child/fanout runs)
  getWorkflowRunLogs: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(
      z.object({
        sections: z.array(
          z.object({
            stepRunId: z.string(),
            stepName: z.string(),
            stepType: z.string(),
            status: z.string(),
            depth: z.number(),
            childRunLabel: z.string().nullable(),
            logs: z.array(
              z
                .object({
                  level: z.number(),
                  time: z.string(),
                  msg: z.string(),
                })
                .passthrough(),
            ),
          }),
        ),
      }),
    )
    .route({
      summary: "Get all logs for a workflow run",
      description:
        "Returns logs grouped by step for a whole workflow run, including child and fanout runs, with step name, type, status and depth.",
      method: "GET",
    }),

  // List available step types
  listStepTypes: oc
    .input(
      z.object({
        workflowDefinitionId: z.uuid().optional(),
        // JSON-Schema serialization of every step's config is ~40% of this
        // response and only the selected step's schema is ever read, so it is
        // opt-in. Callers that need one schema use `getStepTypeConfigSchema`.
        includeConfigSchemas: z.boolean().optional(),
      }),
    )
    .output(ListStepTypesResponseSchema)
    .route({
      summary: "List workflow step types",
      description:
        "Returns the step types available to workflows, optionally for a version and including their config schemas.",
      method: "GET",
    }),

  // JSON Schema for a single step type's config, for the step config panel.
  getStepTypeConfigSchema: oc
    .input(z.object({ type: z.string() }))
    .output(
      z.object({ configSchema: z.record(z.string(), z.unknown()).optional() }),
    )
    .route({
      summary: "Get a step type's config schema",
      description:
        "Returns the configuration JSON Schema for one workflow step type.",
      method: "GET",
    }),

  // List model keys + field metadata available to generic model-mapping workflow UIs
  listUpsertModels: oc
    .input(z.object({}))
    .output(
      z.object({
        models: z.array(
          z.object({
            key: z.string(),
            fields: z.array(
              z.object({
                name: z.string(),
                type: z.enum(["string", "boolean", "date", "number"]),
              }),
            ),
          }),
        ),
      }),
    )
    .route({
      summary: "List records workflows can upsert",
      description:
        "Returns the data models generic upsert workflow steps can map into, with each model's field names and types.",
      method: "GET",
    }),

  // Fields of a task anchor's record that Sync Tasks From Rows can match keys on
  listSyncAnchorMatchFields: oc
    .input(z.object({ entityType: z.enum(SystemEntity) }))
    .output(
      z.object({
        fields: z.array(z.string()),
        hasSourceSystem: z.boolean(),
        sourceSystems: z.array(z.string()),
      }),
    )
    .route({
      summary: "List fields a synced task can link by",
      description:
        "Returns the text fields of an entity's records that Sync Tasks From Rows can match a row's key against, whether those records record their source system, and the source systems the caller's organization has records from. Empty when tasks can't be linked to that entity.",
      method: "GET",
    }),

  listActions: oc
    .input(z.object({}))
    .output(ListActionWorkflowOptionsResponseSchema)
    .route({
      summary: "List reusable workflow actions",
      description:
        "Returns active workflows that can be called as reusable actions from other workflows, with their input/output interface.",
      method: "GET",
    }),

  // List available workflow templates
  listTemplates: oc
    .input(z.object({}))
    .output(ListWorkflowTemplatesResponseSchema)
    .route({
      summary: "List workflow templates",
      description:
        "Returns the prebuilt workflow templates available to start a new workflow from.",
      method: "GET",
    }),

  // Instantiate a workflow template
  instantiateTemplate: oc
    .input(InstantiateWorkflowTemplateRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({
      summary: "Create a workflow from a template",
      description:
        "Creates a new workflow in the organization from a prebuilt template and returns its first version.",
      method: "POST",
    }),

  // List workflow definition summaries (with node count and last run)
  listDefinitionSummaries: oc
    .input(ListWorkflowDefinitionSummariesRequestSchema)
    .output(ListWorkflowDefinitionSummariesResponseSchema)
    .route({
      summary: "Search workflows",
      description:
        "Paginated, sortable workflow list with step count and last run; filters by text, mine, published, paused, scheduled, event-triggered, system or namespace.",
      method: "GET",
    }),

  listGroups: oc
    .input(
      z.object({
        limit: z.number().optional(),
        offset: z.number().optional(),
      }),
    )
    .output(
      z.object({
        groups: z.array(WorkflowGroupSummarySchema),
        total: z.number(),
      }),
    )
    .route({
      summary: "List workflow groups",
      description:
        "Paginated list of workflow groups, which collect related workflows on one canvas.",
      method: "GET",
    }),

  getGroup: oc
    .input(z.object({ workflowGroupId: z.uuid() }))
    .output(WorkflowGroupResponseSchema)
    .route({
      summary: "Get a workflow group",
      description:
        "Returns one workflow group with its member workflows and their canvas positions.",
      method: "GET",
    }),

  createGroup: oc
    .input(CreateWorkflowGroupRequestSchema)
    .output(WorkflowGroupResponseSchema)
    .route({
      summary: "Create a workflow group",
      description:
        "Creates a named group of existing workflows with optional description and canvas positions for each.",
      method: "POST",
    }),

  updateGroup: oc
    .input(UpdateWorkflowGroupRequestSchema)
    .output(WorkflowGroupResponseSchema)
    .route({
      summary: "Update a workflow group",
      description:
        "Changes a workflow group's name, description, member workflows or their canvas positions.",
      method: "PUT",
    }),

  listGroupsForWorkflow: oc
    .input(z.object({ workflowId: z.uuid() }))
    .output(
      z.object({
        groups: z.array(WorkflowGroupSummarySchema),
      }),
    )
    .route({
      summary: "List groups containing a workflow",
      description: "Returns the workflow groups that include a given workflow.",
      method: "GET",
    }),

  // List runs for a workflow definition
  listRunsForDefinition: oc
    .input(ListWorkflowRunsForDefinitionRequestSchema)
    .output(ListWorkflowRunsForDefinitionResponseSchema)
    .route({
      summary: "List runs of a workflow version",
      description:
        "Paginated, sortable runs of a workflow version, filterable by status, trigger type, date range, text, test case or scheduled reruns only.",
      method: "GET",
    }),

  // Cross-workflow run list (the "All Runs" page)
  listAllRuns: oc
    .input(ListAllWorkflowRunsRequestSchema)
    .output(ListAllWorkflowRunsResponseSchema)
    .route({
      summary: "List workflow runs across all workflows",
      description:
        "Paginated runs org-wide, filterable by workflows, status, trigger type, date range or stop reason/run ID; child and system runs optional.",
      method: "GET",
    }),

  // Live in-flight counts per workflow (the All Runs stats strip)
  getRunningRunStats: oc
    .input(z.object({}))
    .output(GetRunningRunStatsResponseSchema)
    .route({
      summary: "Count running workflow runs",
      description:
        "Returns how many workflow runs are executing now, in total and per workflow, including child runs.",
      method: "GET",
    }),

  // List rerun children for a given workflow run
  listRerunsForRun: oc
    .input(ListRerunsForRunRequestSchema)
    .output(ListRerunsForRunResponseSchema)
    .route({
      summary: "List reruns of a workflow run",
      description:
        "Returns the reruns started from a workflow run, with status, timing, who triggered them and stop reason.",
      method: "GET",
    }),

  // Original run → each snooze hop that led to this run
  listSnoozeLineage: oc
    .input(ListSnoozeLineageRequestSchema)
    .output(ListSnoozeLineageResponseSchema)
    .route({
      summary: "Get the snooze history of a workflow run",
      description:
        "Returns the chain from the original run through each snoozed rerun to this run, with status, schedule, snooze reason and attempt.",
      method: "GET",
    }),

  // Org-wide inbox of upcoming (future) scheduled reruns
  listUpcomingScheduledReruns: oc
    .input(ListUpcomingScheduledRerunsRequestSchema)
    .output(ListUpcomingScheduledRerunsResponseSchema)
    .route({
      summary: "List upcoming scheduled workflow reruns",
      description:
        "Org-wide, cursor-paginated list of snoozed reruns waiting to fire, with workflow, scheduled time, who snoozed it, reason and linked record.",
      method: "GET",
    }),

  // Cancel a scheduled rerun before it fires (Temporal-only)
  cancelScheduledRerun: oc
    .input(CancelScheduledRerunRequestSchema)
    .output(CancelScheduledRerunResponseSchema)
    .route({
      summary: "Cancel a scheduled workflow rerun",
      description:
        "Cancels one snoozed workflow rerun before it fires and returns the updated run.",
      method: "POST",
    }),

  // Stop selected active/scheduled runs across a workflow lineage.
  stopWorkflowRuns: oc
    .input(StopWorkflowRunsRequestSchema)
    .output(StopWorkflowRunsResponseSchema)
    .route({
      summary: "Stop runs of a workflow",
      description:
        "Cancels every run of a workflow in the chosen states (running, queued, idle or scheduled); returns stopped, already-finished and failed counts.",
      method: "POST",
    }),

  // What a run's Collect Results step recorded: the succeeded/failed counts and
  // each failed item with its inputs and reason. Null when the run has none.
  getCollectedResults: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(CollectedResultsSchema.nullable())
    .route({
      summary: "Get collected results of a workflow run",
      description:
        "Returns a run's collected succeeded and failed counts and each failed item with its inputs and reason; null if the run has none.",
      method: "GET",
    }),

  // "Re-run failed (N)": start the same workflow for only the items the run's
  // Collect Results recorded as failed, linked to the run as a rerun.
  rerunFailedItems: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(WorkflowRunResponseSchema)
    .route({
      summary: "Rerun failed items of a workflow run",
      description:
        "Starts a new run of the same workflow for only the items the run recorded as failed, linked as a rerun.",
      method: "POST",
    }),

  // Trigger a rerun of an existing workflow run (extracts eventData server-side)
  rerunFromRun: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(WorkflowRunResponseSchema)
    .route({
      summary: "Rerun a workflow run",
      description:
        "Starts a new run of the workflow reusing the original run's trigger data, linked as a rerun.",
      method: "POST",
    }),

  // Dev-only: trigger a draft definition using event data extracted from a
  // past run. Allows iterating on a draft without publishing it each time.
  // Blocked in production environments.
  testDraftWithRun: oc
    .input(
      z.object({
        draftDefinitionId: z.uuid(),
        sourceRunId: z.uuid(),
      }),
    )
    .output(WorkflowRunResponseSchema)
    .route({
      summary: "Test a draft workflow with past run data",
      description:
        "Starts a run of an unpublished draft version using trigger data from a past run; disabled in production.",
      method: "POST",
    }),

  // Retry a failed or timed_out step
  retryStep: oc
    .input(RetryStepRequestSchema)
    .output(RetryStepResponseSchema)
    .route({
      summary: "Retry a failed workflow step",
      description:
        "Re-executes a failed or timed-out step in a workflow run, optionally with overridden input, and continues the run.",
      method: "POST",
    }),

  // Restart workflow from a specific step (cancels downstream steps)
  restartFromStep: oc
    .input(RestartFromStepRequestSchema)
    .output(RestartFromStepResponseSchema)
    .route({
      summary: "Restart a workflow run from a step",
      description:
        "Re-executes a workflow run from a chosen step, optionally with new input, cancelling downstream step results.",
      method: "POST",
    }),

  // Resolve a guardrail review (confirm violation or dismiss as false positive)
  resolveGuardrailReview: oc
    .input(ResolveGuardrailReviewRequestSchema)
    .output(ResolveGuardrailReviewResponseSchema)
    .route({
      summary: "Resolve a workflow guardrail review",
      description:
        "Confirms a flagged guardrail violation, blocking the step, or dismisses it as a false positive so the step retries; optional comment.",
      method: "POST",
    }),

  // Get workflow run and executed steps for a specific entity
  getEntityWorkflowContext: oc
    .input(GetEntityWorkflowContextRequestSchema)
    .output(GetEntityWorkflowContextResponseSchema)
    .route({
      summary: "Get the workflow run behind a record",
      description:
        "For a business record, returns the workflow run that processed it, its executed steps with latest results, and the parent run.",
      method: "GET",
    }),

  // Get child workflow runs for a parent workflow
  getChildWorkflowRuns: oc
    .input(z.object({ parentWorkflowRunId: z.uuid() }))
    .output(
      z.object({
        childWorkflowRuns: z.array(WorkflowRunResponseSchema),
      }),
    )
    .route({
      summary: "List child runs of a workflow run",
      description:
        "Returns the direct child runs (fanout or called actions) started by a workflow run.",
      method: "GET",
    }),

  // Get the entire descendant run tree for a root run in one call: every
  // child run at any depth, all of their step runs, and the step metadata for
  // each distinct definition. Replaces the per-node client fanout the run
  // detail page used to do (one getChildWorkflowRuns + getStepRuns per node).
  getRunTree: oc
    .input(z.object({ rootWorkflowRunId: z.uuid() }))
    .output(
      z.object({
        // All descendant runs (excludes the root). Each carries its
        // parentWorkflowRunId so the client can rebuild the parent→children map.
        childRuns: z.array(WorkflowRunResponseSchema),
        // Step runs across every descendant run (keyed by workflowRunId).
        stepRuns: z.array(StepRunResponseSchema),
        // Minimal step metadata per distinct descendant definition, used to
        // label steps in the trace tree.
        stepDefinitions: z.array(
          z.object({
            workflowDefinitionId: z.uuid(),
            steps: z.array(
              z.object({
                id: z.uuid(),
                name: z.string(),
                type: z.string(),
                category: z.string(),
              }),
            ),
          }),
        ),
      }),
    )
    .route({
      summary: "Get a workflow run's full run tree",
      description:
        "Returns every descendant run of a root workflow run at any depth, all their step runs, and step names for labeling.",
      method: "GET",
    }),

  // Duplicate a workflow definition
  duplicateDefinition: oc
    .input(DuplicateWorkflowDefinitionRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({
      summary: "Duplicate a workflow",
      description:
        "Copies a workflow version into a new, inactive workflow with the given name.",
      method: "POST",
    }),

  // Test a single workflow step in isolation (admin only)
  testStep: oc
    .input(
      z.object({
        stepType: z.string(),
        input: z.record(z.string(), z.unknown()),
        config: z.record(z.string(), z.unknown()),
        timeoutMs: z.number().int().min(1000).max(300_000).optional(),
      }),
    )
    .output(
      z.object({
        success: z.boolean(),
        output: z.record(z.string(), z.unknown()).optional(),
        emittedEvents: z
          .array(
            z.object({
              eventType: z.string(),
              data: z.unknown(),
            }),
          )
          .optional(),
        error: z.string().optional(),
        durationMs: z.number().optional(),
      }),
    )
    .route({
      summary: "Test a single workflow step",
      description:
        "Admin-only: executes one step type with given input and config outside any workflow, using real services; returns output, emitted events, error and duration.",
      method: "POST",
    }),

  // Delete a workflow definition (soft delete)
  deleteDefinition: oc
    .input(z.object({ workflowDefinitionId: z.uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Delete a workflow version",
      description: "Soft-deletes one workflow version.",
      method: "DELETE",
    }),

  // Delete a whole workflow (lineage row) — cascades a soft-delete across
  // every version, run, step, edge, and step run under it. Used by the
  // list-page row action, which operates on workflow ids (not versions).
  deleteWorkflow: oc
    .input(z.object({ workflowId: z.uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Delete a workflow",
      description:
        "Soft-deletes a whole workflow with all its versions, runs and step history.",
      method: "DELETE",
    }),

  // Test case CRUD
  listTestCases: oc
    .input(ListTestCasesRequestSchema)
    .output(z.object({ testCases: z.array(WorkflowTestCaseSchema) }))
    .route({
      summary: "List saved test cases for a workflow",
      description:
        "Returns saved manual test cases for a workflow version, each with name and trigger event data.",
      method: "GET",
    }),

  createTestCase: oc
    .input(CreateTestCaseRequestSchema)
    .output(WorkflowTestCaseSchema)
    .route({
      summary: "Save a workflow test case",
      description:
        "Saves a named test case with trigger event data for manually testing a workflow version.",
      method: "POST",
    }),

  updateTestCase: oc
    .input(UpdateTestCaseRequestSchema)
    .output(WorkflowTestCaseSchema)
    .route({
      summary: "Edit a workflow test case",
      description:
        "Changes a saved workflow test case's name, description or trigger event data.",
      method: "PUT",
    }),

  deleteTestCase: oc
    .input(DeleteTestCaseRequestSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({
      summary: "Delete a workflow test case",
      description: "Deletes a saved workflow test case.",
      method: "DELETE",
    }),

  runTestCase: oc
    .input(RunTestCaseRequestSchema)
    .output(WorkflowRunResponseSchema)
    .route({
      summary: "Run a workflow test case",
      description:
        "Starts a workflow run using a saved test case's event data, from a chosen trigger step when there are several; returns the run.",
      method: "POST",
    }),

  checkTestCaseCompatibility: oc
    .input(CheckTestCaseCompatibilityRequestSchema)
    .output(CheckTestCaseCompatibilityResponseSchema)
    .route({
      summary: "Check test cases against edited steps",
      description:
        "Splits a workflow's saved test cases into those compatible and incompatible with an edited set of steps.",
      method: "POST",
    }),

  // Cancel an active workflow run
  cancelWorkflowRun: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(WorkflowRunResponseSchema)
    .route({
      summary: "Cancel a workflow run",
      description:
        "Cancels an active workflow run and returns its updated state.",
      method: "POST",
    }),

  // Sync Temporal run statuses for active runs
  syncTemporalRunStatuses: oc
    .input(
      z.object({
        workflowRunIds: z.array(z.uuid()).max(50),
      }),
    )
    .output(
      z.object({
        statuses: z.record(
          z.string(),
          z.object({
            status: WorkflowRunStatusSchema,
            stopReason: z.string().optional(),
          }),
        ),
      }),
    )
    .route({
      summary: "Refresh status of active workflow runs",
      description:
        "For up to 50 active runs and their children, reads the orchestrator's current status and updates stale run statuses; returns statuses.",
      method: "POST",
    }),

  // Infrastructure health check
  orchestratorHealth: oc
    .input(z.object({}))
    .output(
      z.object({
        rabbitmq: z.boolean(),
        temporal: z.boolean(),
      }),
    )
    .route({
      summary: "Check workflow engine health",
      description:
        "Reports whether the message queue and workflow orchestrator are reachable.",
      method: "GET",
    }),

  // Rerun a set of AP invoices immediately by re-triggering their originating workflow runs
  rerunInvoices: oc
    .input(z.object({ invoiceIds: z.array(z.uuid()).min(1).max(100) }))
    .output(
      z.object({
        results: z.array(
          z.object({
            invoiceId: z.uuid(),
            success: z.boolean(),
            error: z.string().optional(),
          }),
        ),
      }),
    )
    .route({
      summary: "Rerun processing for supplier invoices",
      description:
        "Immediately reruns the workflow runs that created up to 100 supplier invoices; requires invoice update permission. Returns per-invoice success.",
      method: "POST",
    }),

  // Schedule a future rerun for a set of AP invoices
  snoozeRerunInvoices: oc
    .input(
      z.object({
        invoiceIds: z.array(z.uuid()).min(1).max(100),
        scheduledAt: z.coerce.date(),
      }),
    )
    .output(
      z.object({
        results: z.array(
          z.object({
            invoiceId: z.uuid(),
            success: z.boolean(),
            error: z.string().optional(),
          }),
        ),
      }),
    )
    .route({
      summary: "Schedule a rerun for supplier invoices",
      description:
        "Schedules future reruns of the workflows that created up to 100 supplier invoices at a given time, replacing existing schedules; returns per-invoice success.",
      method: "POST",
    }),

  // -------------------------------------------------------------------------
  // Versioning
  // -------------------------------------------------------------------------

  /**
   * Fork a new draft off the lineage's currently-pinned version. If the
   * caller already has an open draft in this lineage, return it instead of
   * creating another one (prevents accidental duplicates per user).
   */
  createDraft: oc
    .input(CreateDraftRequestSchema)
    .output(CreateDraftResponseSchema)
    .route({
      summary: "Create a workflow draft",
      description:
        "Creates a draft version from the workflow's pinned version, or returns the caller's existing open draft.",
      method: "POST",
    }),

  /**
   * Promote a draft to a finalized, immutable version. Assigns version number,
   * triggers async AI changelog generation. If the draft's parent is no longer
   * pinned, returns a stale-base warning; caller must retry with
   * acknowledgeStaleBase=true to proceed.
   */
  finalizeVersion: oc
    .input(FinalizeVersionRequestSchema)
    .output(FinalizeVersionResponseSchema)
    .route({
      summary: "Finalize a workflow draft",
      description:
        "Turns a draft into an immutable numbered version and starts AI changelog generation; warns when its base is no longer pinned unless acknowledged.",
      method: "POST",
    }),

  /**
   * Mark a finalized version as the canonical version of its lineage.
   * Transactionally unpins the previously-pinned version and re-syncs Temporal
   * schedules.
   */
  pinVersion: oc
    .input(PinVersionRequestSchema)
    .output(PinVersionResponseSchema)
    .route({
      summary: "Publish a workflow version",
      description:
        "Makes a finalized version the workflow's live pinned version used by triggers, unpinning the previous one and updating schedules.",
      method: "POST",
    }),

  /**
   * Update the changelog text on any version. This is the only field that
   * may be mutated on a finalized version.
   */
  updateVersionChangelog: oc
    .input(UpdateVersionChangelogRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({
      summary: "Edit a workflow version changelog",
      description:
        "Updates the changelog text of any workflow version, the only field editable after finalizing.",
      method: "PATCH",
    }),

  /** List all versions for a lineage, ordered by version DESC. */
  listVersions: oc
    .input(ListVersionsRequestSchema)
    .output(ListVersionsResponseSchema)
    .route({
      summary: "List versions of a workflow",
      description:
        "Returns a workflow's versions, newest first, with status, version number, changelog and which is pinned.",
      method: "GET",
    }),

  /** Audit trail of a lineage, read off `audit_change_log`; newest first. */
  listActivity: oc
    .input(ListWorkflowActivityRequestSchema)
    .output(ListWorkflowActivityResponseSchema)
    .route({
      method: "GET",
      summary: "List a workflow's activity timeline",
      description:
        "Audit trail of a workflow lineage, newest first: version drafts, edits, finalizations, publishes (including automatic rollbacks), name/active changes and deletions, each with the acting user and field diffs.",
    }),

  /** Structural diff between two versions (steps/edges added, removed, modified). */
  compareVersions: oc
    .input(CompareWorkflowVersionsRequestSchema)
    .output(CompareWorkflowVersionsResponseSchema)
    .route({
      method: "GET",
      summary: "Compare two workflow versions",
      description:
        "Structural diff between two versions of a workflow: steps and edges added, removed or modified.",
    }),

  previewVersionDiff: oc
    .input(PreviewVersionDiffRequestSchema)
    .output(PreviewVersionDiffResponseSchema)
    .route({
      method: "GET",
      summary: "Preview a workflow draft's changes",
      description:
        "Compares a draft with its parent and the current pinned version before publishing.",
    }),

  getVersionJsonDiff: oc
    .input(VersionJsonDiffRequestSchema)
    .output(VersionJsonDiffResponseSchema)
    .route({
      method: "GET",
      summary: "Read canonical workflow version JSON",
      description:
        "Staff-only canonical documents for comparing two versions in the same workflow.",
    }),

  /** Step catalog for authoring: searchable, compact, visibility-filtered. */
  catalog: {
    searchStepTypes: oc
      .input(SearchStepTypesRequestSchema)
      .output(SearchStepTypesResponseSchema)
      .route({
        method: "GET",
        summary: "Search workflow step types",
        description:
          "List visible, non-deprecated workflow step types, optionally filtered by text or category. Inspect a type for its sockets and config schema.",
      }),
    inspectStepType: oc
      .input(InspectStepTypeRequestSchema)
      .output(InspectStepTypeResponseSchema)
      .route({
        method: "GET",
        summary: "Inspect a workflow step type",
        description:
          "Input/output sockets and config JSON Schema for one step type. Call before adding the step or wiring edges to it.",
      }),
    getEngineHelp: oc
      .input(GetWorkflowEngineHelpRequestSchema)
      .output(GetWorkflowEngineHelpResponseSchema)
      .route({
        method: "GET",
        summary: "Workflow engine help",
        description:
          "Detailed guidance on a workflow engine concept (sockets, readiness, fanout, triggers, static inputs, dynamic sockets, email pattern, code sandbox, voice agent extraction).",
      }),
  },

  /** Reference data needed to configure steps. */
  lookups: {
    listCoreEnums: oc
      .input(ListCoreEnumsLookupRequestSchema)
      .output(ListCoreEnumsLookupResponseSchema)
      .route({
        method: "GET",
        summary: "List core enums",
        description:
          "Core enums with ids and options. Use to find the coreEnumId for a core_enum_fanout step.",
      }),
    listSystemEvents: oc
      .input(z.object({}))
      .output(ListSystemEventsLookupResponseSchema)
      .route({
        method: "GET",
        summary: "List system events",
        description:
          "System event types usable with event_trigger steps, with their payload fields.",
      }),
  },

  /** List draft versions in the org (optionally filtered to the caller). */
  listDrafts: oc
    .input(ListDraftsRequestSchema)
    .output(ListDraftsResponseSchema)
    .route({
      summary: "List workflow drafts",
      description:
        "Returns draft workflow versions, by default only the caller's; optionally every draft in the organization.",
      method: "GET",
    }),

  /** Soft-delete a draft. Only the draft's creator may delete it. Finalized
   *  versions can never be deleted through this endpoint — they're immutable
   *  history (past runs reference them by id). */
  deleteDraft: oc
    .input(DeleteDraftRequestSchema)
    .output(DeleteDraftResponseSchema)
    .route({
      summary: "Delete a workflow draft",
      description:
        "Soft-deletes a draft version (creator only); if it was the workflow's only version, deletes the workflow too.",
      method: "DELETE",
    }),

  /** Re-run the AI changelog generator on a finalized version. Useful when
   *  the original generation fell back to the mechanical summary (e.g. the
   *  model was down at finalize time). */
  regenerateChangelog: oc
    .input(RegenerateChangelogRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({
      summary: "Regenerate a workflow version changelog",
      description:
        "Reruns AI changelog generation for a finalized workflow version and returns the updated version.",
      method: "POST",
    }),

  // Submit a workflow feature request email (fired from chat agent confirmation card)
  submitFeatureRequest: oc
    .input(
      z.object({
        useCase: z.string().trim().min(1).max(2000),
        stepsConsidered: z.array(z.string().trim().min(1).max(200)).max(20),
      }),
    )
    .output(
      z.object({
        success: z.boolean(),
        message: z.string(),
      }),
    )
    .route({
      summary: "Send a workflow feature request",
      description:
        "Emails the use case and steps considered to the support team, copying the requester, as a request for new workflow capabilities.",
      method: "POST",
    }),

  // AI-assisted step config modification (POC)
  assistStepConfig: oc
    .input(
      z.object({
        stepType: z.string(),
        currentConfig: z.record(z.string(), z.unknown()),
        configSchema: z.record(z.string(), z.unknown()).optional(),
        stepDescription: z.string().optional(),
        instruction: z.string().min(1).max(2000),
      }),
    )
    .output(
      z.object({
        config: z.record(z.string(), z.unknown()),
        explanation: z.string(),
      }),
    )
    .route({
      summary: "Edit a step config with AI",
      description:
        "Uses AI to apply a plain-language instruction to a workflow step's current config and returns the new config with an explanation; saves nothing.",
      method: "POST",
    }),
  submitWorkflowApproval: oc
    .input(
      z.object({
        stepRunId: z.uuid(),
        decision: z.enum(["approved", "rejected", "escalated"]),
        comment: z.string().optional(),
        awaitingRevision: z
          .boolean()
          .optional()
          .describe(
            "Sales-order rejections only: the order is rejected while a corrected PO is expected from the customer.",
          ),
        escalationPath: z
          .string()
          .optional()
          .describe(
            "When decision is 'escalated', the named escalation path socket to route to (from the step's escalationPaths config).",
          ),
      }),
    )
    .output(
      z.object({
        success: z.literal(true),
      }),
    )
    .route({
      summary: "Approve or reject a workflow approval",
      description:
        "Records an approve, reject or escalate decision with comment on a workflow approval step, then continues or re-triggers the workflow; approver or admin only.",
      method: "POST",
    }),
  // Admin-only: dismiss a pending approval item (e.g. one whose subject was
  // deleted) — flips the gate's raised decision to "dismissed" and archives
  // its request notifications for every recipient.
  dismissWorkflowApproval: oc
    .input(
      z.object({
        stepRunId: z.uuid(),
      }),
    )
    .output(
      z.object({
        success: z.literal(true),
      }),
    )
    .route({
      summary: "Dismiss a pending workflow approval",
      description:
        "Admin-only: marks a pending approval as dismissed, e.g. when its record was deleted, and archives its request notifications for every recipient.",
      method: "POST",
    }),
  listPendingApprovals: oc
    .input(ListPendingApprovalsRequestSchema)
    .output(ListPendingApprovalsResponseSchema)
    .route({
      summary: "List my pending workflow approvals",
      description:
        "Paginated approvals awaiting the caller's decision, with workflow, step, instructions and order or discount-group summary; admins see all.",
      method: "GET",
    }),
  getAutomationScore: oc
    .input(GetAutomationScoreRequestSchema)
    .output(GetAutomationScoreResponseSchema)
    .route({
      summary: "Get a workflow's automation score",
      description:
        "Returns the share of completed runs needing no human approval, overall, last 7 days, weekly and per version, with approval step skip rates.",
      method: "GET",
    }),
} as const;

export const SubmitWorkflowApprovalRequest = z.object({
  stepRunId: z.uuid(),
  decision: z.enum(["approved", "rejected", "escalated"]),
  comment: z.string().optional(),
  awaitingRevision: z.boolean().optional(),
  escalationPath: z.string().optional(),
});
export type SubmitWorkflowApprovalRequest = z.infer<
  typeof SubmitWorkflowApprovalRequest
>;

/**
 * Export types
 */
export type CreateWorkflowDefinitionRequest = z.infer<
  typeof CreateWorkflowDefinitionRequestSchema
>;
export type UpdateWorkflowDefinitionRequest = z.infer<
  typeof UpdateWorkflowDefinitionRequestSchema
>;
export type ValidateWorkflowDefinitionRequest = z.infer<
  typeof ValidateWorkflowDefinitionRequestSchema
>;
export type ValidateWorkflowDefinitionResponse = z.infer<
  typeof ValidateWorkflowDefinitionResponseSchema
>;
export type WorkflowValidationError = z.infer<
  typeof WorkflowValidationErrorSchema
>;
export type WorkflowDefinitionResponse = z.infer<
  typeof WorkflowDefinitionResponseSchema
>;
export type WorkflowResponse = z.infer<typeof WorkflowResponseSchema>;
export type UpdateWorkflowRequest = z.infer<typeof UpdateWorkflowRequestSchema>;
export type TriggerWorkflowRequest = z.infer<
  typeof TriggerWorkflowRequestSchema
>;
export type WorkflowRunResponse = z.infer<typeof WorkflowRunResponseSchema>;
export type StopWorkflowRunsRequest = z.infer<
  typeof StopWorkflowRunsRequestSchema
>;
export type StopWorkflowRunsResponse = z.infer<
  typeof StopWorkflowRunsResponseSchema
>;
export type GetWorkflowRunRequest = z.infer<typeof GetWorkflowRunRequestSchema>;
export type StepRunResponse = z.infer<typeof StepRunResponseSchema>;
export type ListStepTypesResponse = z.infer<typeof ListStepTypesResponseSchema>;
export type ActionWorkflowInterface = z.infer<
  typeof ActionWorkflowInterfaceSchema
>;
export type ActionWorkflowOption = z.infer<typeof ActionWorkflowOptionSchema>;
export type ListActionWorkflowOptionsResponse = z.infer<
  typeof ListActionWorkflowOptionsResponseSchema
>;
export type WorkflowTemplateResponse = z.infer<
  typeof WorkflowTemplateResponseSchema
>;
export type ListWorkflowTemplatesResponse = z.infer<
  typeof ListWorkflowTemplatesResponseSchema
>;
export type InstantiateWorkflowTemplateRequest = z.infer<
  typeof InstantiateWorkflowTemplateRequestSchema
>;
export type WorkflowDefinitionSummary = z.infer<
  typeof WorkflowDefinitionSummarySchema
>;
export type ListWorkflowDefinitionSummariesRequest = z.infer<
  typeof ListWorkflowDefinitionSummariesRequestSchema
>;
export type ListWorkflowDefinitionSummariesResponse = z.infer<
  typeof ListWorkflowDefinitionSummariesResponseSchema
>;
export type WorkflowRunDetailStepRun = z.infer<
  typeof WorkflowRunDetailStepRunSchema
>;
export type GetWorkflowRunDetailsResponse = z.infer<
  typeof GetWorkflowRunDetailsResponseSchema
>;
export type GetWorkflowStepRunResponse = z.infer<
  typeof GetWorkflowStepRunResponseSchema
>;
export type WorkflowGroupFrame = z.infer<typeof WorkflowGroupFrameSchema>;
export type WorkflowGroupSummary = z.infer<typeof WorkflowGroupSummarySchema>;
export type WorkflowGroupImplicitLink = z.infer<
  typeof WorkflowGroupImplicitLinkSchema
>;
export type WorkflowGroupResponse = z.infer<typeof WorkflowGroupResponseSchema>;
export type WorkflowGroupMember = z.infer<typeof WorkflowGroupMemberSchema>;
export type CreateWorkflowGroupRequest = z.infer<
  typeof CreateWorkflowGroupRequestSchema
>;
export type UpdateWorkflowGroupRequest = z.infer<
  typeof UpdateWorkflowGroupRequestSchema
>;
export type WorkflowRunListSortField = z.infer<
  typeof WorkflowRunListSortFieldSchema
>;
export type ListWorkflowRunsForDefinitionResponse = z.infer<
  typeof ListWorkflowRunsForDefinitionResponseSchema
>;
export type ListWorkflowRunsForDefinitionRequest = z.infer<
  typeof ListWorkflowRunsForDefinitionRequestSchema
>;
export type ListAllWorkflowRunsRequest = z.infer<
  typeof ListAllWorkflowRunsRequestSchema
>;
export type ListAllWorkflowRunsResponse = z.infer<
  typeof ListAllWorkflowRunsResponseSchema
>;
export type RunningWorkflowRunStat = z.infer<
  typeof RunningWorkflowRunStatSchema
>;
export type GetRunningRunStatsResponse = z.infer<
  typeof GetRunningRunStatsResponseSchema
>;
export type ListRerunsForRunRequest = z.infer<
  typeof ListRerunsForRunRequestSchema
>;
export type RerunSummary = z.infer<typeof RerunSummarySchema>;
export type ListRerunsForRunResponse = z.infer<
  typeof ListRerunsForRunResponseSchema
>;
export type ListSnoozeLineageRequest = z.infer<
  typeof ListSnoozeLineageRequestSchema
>;
export type SnoozeLineageHop = z.infer<typeof SnoozeLineageHopSchema>;
export type ListSnoozeLineageResponse = z.infer<
  typeof ListSnoozeLineageResponseSchema
>;
export type ListUpcomingScheduledRerunsRequest = z.infer<
  typeof ListUpcomingScheduledRerunsRequestSchema
>;
export type UpcomingScheduledRerun = z.infer<
  typeof UpcomingScheduledRerunSchema
>;
export type ListUpcomingScheduledRerunsResponse = z.infer<
  typeof ListUpcomingScheduledRerunsResponseSchema
>;
export type CancelScheduledRerunRequest = z.infer<
  typeof CancelScheduledRerunRequestSchema
>;
export type CancelScheduledRerunResponse = z.infer<
  typeof CancelScheduledRerunResponseSchema
>;
export type RetryStepRequest = z.infer<typeof RetryStepRequestSchema>;
export type RetryStepResponse = z.infer<typeof RetryStepResponseSchema>;
export type ResolveGuardrailReviewRequest = z.infer<
  typeof ResolveGuardrailReviewRequestSchema
>;
export type ResolveGuardrailReviewResponse = z.infer<
  typeof ResolveGuardrailReviewResponseSchema
>;
export type RestartFromStepRequest = z.infer<
  typeof RestartFromStepRequestSchema
>;
export type RestartFromStepResponse = z.infer<
  typeof RestartFromStepResponseSchema
>;
export type GetEntityWorkflowContextRequest = z.infer<
  typeof GetEntityWorkflowContextRequestSchema
>;
export type EntityWorkflowExecutedStep = z.infer<
  typeof EntityWorkflowExecutedStepSchema
>;
export type GetEntityWorkflowContextResponse = z.infer<
  typeof GetEntityWorkflowContextResponseSchema
>;
export type DuplicateWorkflowDefinitionRequest = z.infer<
  typeof DuplicateWorkflowDefinitionRequestSchema
>;
export type CheckTestCaseCompatibilityResponse = z.infer<
  typeof CheckTestCaseCompatibilityResponseSchema
>;

// Versioning types
export type WorkflowDefinitionStatus = z.infer<
  typeof WorkflowDefinitionStatusSchema
>;
export type WorkflowVersionSummary = z.infer<
  typeof WorkflowVersionSummarySchema
>;
export type CreateDraftRequest = z.infer<typeof CreateDraftRequestSchema>;
export type CreateDraftResponse = z.infer<typeof CreateDraftResponseSchema>;
export type FinalizeVersionRequest = z.infer<
  typeof FinalizeVersionRequestSchema
>;
export type FinalizeVersionResponse = z.infer<
  typeof FinalizeVersionResponseSchema
>;
export type PinVersionRequest = z.infer<typeof PinVersionRequestSchema>;
export type PinVersionResponse = z.infer<typeof PinVersionResponseSchema>;
export type UpdateVersionChangelogRequest = z.infer<
  typeof UpdateVersionChangelogRequestSchema
>;
export type ListVersionsRequest = z.infer<typeof ListVersionsRequestSchema>;
export type ListVersionsResponse = z.infer<typeof ListVersionsResponseSchema>;
export type ListDraftsRequest = z.infer<typeof ListDraftsRequestSchema>;
export type ListDraftsResponse = z.infer<typeof ListDraftsResponseSchema>;
export type DeleteDraftRequest = z.infer<typeof DeleteDraftRequestSchema>;
export type DeleteDraftResponse = z.infer<typeof DeleteDraftResponseSchema>;
export type RegenerateChangelogRequest = z.infer<
  typeof RegenerateChangelogRequestSchema
>;
export type StaleBaseWarning = z.infer<typeof StaleBaseWarningSchema>;
export type WorkflowActivityKind = z.infer<typeof WorkflowActivityKindSchema>;
export type WorkflowActivityActor = z.infer<typeof WorkflowActivityActorSchema>;
export type WorkflowActivityVersionRef = z.infer<
  typeof WorkflowActivityVersionRefSchema
>;
export type WorkflowActivityFieldDiff = z.infer<
  typeof WorkflowActivityFieldDiffSchema
>;
export type WorkflowActivityGraphChanges = z.infer<
  typeof WorkflowActivityGraphChangesSchema
>;
export type WorkflowActivityEntry = z.infer<typeof WorkflowActivityEntrySchema>;
export type ListWorkflowActivityRequest = z.infer<
  typeof ListWorkflowActivityRequestSchema
>;
export type ListWorkflowActivityResponse = z.infer<
  typeof ListWorkflowActivityResponseSchema
>;

export type StructuredDiff = z.infer<typeof StructuredDiffSchema>;
export type WorkflowEditTrail = z.infer<typeof WorkflowEditTrailSchema>;
export type DraftFreshness = z.infer<typeof DraftFreshnessSchema>;
export type PreviewVersionDiffRequest = z.infer<
  typeof PreviewVersionDiffRequestSchema
>;
export type PreviewVersionDiffResponse = z.infer<
  typeof PreviewVersionDiffResponseSchema
>;
export type VersionJsonDiffRequest = z.infer<
  typeof VersionJsonDiffRequestSchema
>;
export type VersionJsonDiffResponse = z.infer<
  typeof VersionJsonDiffResponseSchema
>;
export type WorkflowCanonicalJson = z.infer<typeof WorkflowCanonicalJsonSchema>;
